import React, { useEffect, useMemo, useState } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import {
  ClipboardList, Package, CheckCircle2, AlertTriangle,
  Filter, FileSpreadsheet, RefreshCw, Eye, Pencil, XCircle, Trash2,
  Bell, PhoneOff, RotateCcw, Calendar, CreditCard, PackageCheck,
  MoreVertical, ChevronLeft, ChevronRight, ChevronDown,
  Wrench, Siren, FileText, Activity, User2, Car,
  Download, Clock, Wallet, Plus, Paperclip,
} from "lucide-react";
import { toast } from "sonner";
import { useApp } from "@/context/AppContext";
import {
  listOrders, markOrdered, receiveParts, updateEdd, markInvoiced,
  sendReminder, cnrOrder, returnToStock, cancelOrder, deleteOrder,
  previewTemplate, generateTemplate, markOrderedBulk, workbenchCounts,
  previewOrderExcel, generateOrderExcel,
  setOrderAdvance, deleteOrderAdvance,
  downloadOrderAttachmentUrl,
} from "@/lib/api";
import {
  fmtDate, fmtDateTime, STATUS_LABELS, STATUS_COLORS, todayISO, ROLE_LABELS,
} from "@/lib/format";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Sheet, SheetContent, SheetHeader, SheetTitle, SheetFooter,
} from "@/components/ui/sheet";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import {
  DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem,
  DropdownMenuSeparator,
} from "@/components/ui/dropdown-menu";

const TABS = [
  { key: "new_order", label: "New Orders", icon: ClipboardList },
  { key: "ordered", label: "Ordered", icon: Package },
  { key: "pending_pickup", label: "Pending Pickup", icon: CheckCircle2 },
  { key: "delayed", label: "Delayed", icon: AlertTriangle },
  { key: "completed", label: "Completed", icon: CreditCard },
  { key: "unclaimed", label: "Unclaimed", icon: PhoneOff },
];

// Ordered-tab chip filters
const ORDERED_CHIPS = [
  { key: "ALL", label: "All" },
  { key: "today", label: "Arriving Today" },
  { key: "tomorrow", label: "Arriving Tomorrow" },
  { key: "in2", label: "Arriving in 2 Days" },
  { key: "in3", label: "Arriving in 3 Days" },
  { key: "delayed", label: "Delayed" },
  { key: "urgent", label: "Urgent" },
  { key: "partial", label: "Partially Arrived" },
  { key: "backordered", label: "Backordered" },
];

const EDD_REASONS = [
  "Yamaha Backorder",
  "Transit Delay",
  "Area Warehouse Delay",
  "Wrong Order Raised",
  "Supplier Delay",
  "Other",
];

// Compute arrival status from EDD
const arrivalStatus = (eddIso) => {
  if (!eddIso) return null;
  try {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const edd = new Date(eddIso);
    edd.setHours(0, 0, 0, 0);
    const diff = Math.round((edd - today) / 86400000);
    if (diff < 0) return { kind: "delayed", days: Math.abs(diff), label: `Delayed by ${Math.abs(diff)} ${Math.abs(diff) === 1 ? "Day" : "Days"}`, color: "bg-rose-100 text-rose-700", dot: "bg-rose-500" };
    if (diff === 0) return { kind: "today", days: 0, label: "Arriving Today", color: "bg-amber-100 text-amber-800", dot: "bg-amber-500" };
    if (diff === 1) return { kind: "tomorrow", days: 1, label: "Arriving Tomorrow", color: "bg-yellow-100 text-yellow-800", dot: "bg-yellow-500" };
    return { kind: "upcoming", days: diff, label: `Arrives in ${diff} ${diff === 1 ? "Day" : "Days"}`, color: "bg-emerald-100 text-emerald-700", dot: "bg-emerald-500" };
  } catch {
    return null;
  }
};

// Bucket order for Ordered tab sorting
// 0 = delayed (highest delay first), 1 = today, 2 = tomorrow,
// 3 = 2 days, 4 = 3 days, 5 = 4-7 days, 6 = 8+ days, 7 = no EDD
const arrivalBucket = (a) => {
  if (!a) return 7;
  if (a.kind === "delayed") return 0;
  if (a.kind === "today") return 1;
  if (a.kind === "tomorrow") return 2;
  if (a.days === 2) return 3;
  if (a.days === 3) return 4;
  if (a.days >= 4 && a.days <= 7) return 5;
  return 6;
};

const sortOrderedRows = (rows) => {
  return [...rows].sort((a, b) => {
    const aArr = arrivalStatus(a.edd);
    const bArr = arrivalStatus(b.edd);
    const aB = arrivalBucket(aArr);
    const bB = arrivalBucket(bArr);
    if (aB !== bB) return aB - bB;
    // Within delayed: highest delay first
    if (aB === 0) {
      if (aArr.days !== bArr.days) return bArr.days - aArr.days;
    }
    // Urgent first
    if (a.priority !== b.priority) return a.priority === "urgent" ? -1 : 1;
    // Older orders first (created_at ascending)
    const ca = new Date(a.created_at).getTime();
    const cb = new Date(b.created_at).getTime();
    if (ca !== cb) return ca - cb;
    // Nearest EDD first
    const ea = a.edd ? new Date(a.edd).getTime() : Infinity;
    const eb = b.edd ? new Date(b.edd).getTime() : Infinity;
    return ea - eb;
  });
};

const SUMMARY_CARDS = [
  { key: "new_order", label: "New Orders", icon: ClipboardList, tabKey: "new_order", iconColor: "text-blue-600", accent: "border-l-blue-500" },
  { key: "ordered", label: "Ordered", icon: Package, tabKey: "ordered", iconColor: "text-amber-600", accent: "border-l-amber-500" },
  { key: "pending_pickup", label: "Pending Pickup", icon: CheckCircle2, tabKey: "pending_pickup", iconColor: "text-emerald-600", accent: "border-l-emerald-500" },
  { key: "delayed", label: "Delayed", icon: AlertTriangle, tabKey: "delayed", iconColor: "text-rose-600", accent: "border-l-rose-500" },
  { key: "urgent", label: "Urgent Orders", icon: Siren, tabKey: "new_order", iconColor: "text-red-600", accent: "border-l-red-500", filterPriority: "urgent" },
  { key: "customer_not_responding", label: "Follow-up Active", icon: PhoneOff, tabKey: "pending_pickup", iconColor: "text-purple-600", accent: "border-l-purple-500" },
  { key: "unclaimed", label: "Unclaimed", icon: PhoneOff, tabKey: "unclaimed", iconColor: "text-rose-600", accent: "border-l-rose-500" },
];

const PAGE_SIZE = 8;

const Workbench = () => {
  const { currentUser, currentLocation, locations } = useApp();
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();

  const filterParam = searchParams.get("filter");
  const initialTab = useMemo(() => {
    if (["new_order", "ordered", "pending_pickup", "delayed", "completed", "unclaimed"].includes(filterParam)) return filterParam;
    return "new_order";
  }, [filterParam]);

  const [activeTab, setActiveTab] = useState(initialTab);
  const [orders, setOrders] = useState([]);
  const [counts, setCounts] = useState({});
  const [selected, setSelected] = useState({});
  const [expandedId, setExpandedId] = useState(null);
  const [q, setQ] = useState(searchParams.get("q") || "");
  const [filterPriority, setFilterPriority] = useState("ALL");
  const [filterOrderType, setFilterOrderType] = useState("ALL");
  const [filterModel, setFilterModel] = useState("");
  const [filterLocation, setFilterLocation] = useState(searchParams.get("location") || "ALL");
  const [filterFromDate, setFilterFromDate] = useState("");
  const [filterToDate, setFilterToDate] = useState("");
  const [orderedChip, setOrderedChip] = useState("ALL");
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(false);

  // Drawers / dialogs
  const [markOrderedDrawer, setMarkOrderedDrawer] = useState(null);
  const [receiveDrawer, setReceiveDrawer] = useState(null);
  const [eddDialog, setEddDialog] = useState(null);
  const [templateDialog, setTemplateDialog] = useState(null);
  const [viewOrder, setViewOrder] = useState(null);
  const [invoiceDrawer, setInvoiceDrawer] = useState(null);
  const [reminderDrawer, setReminderDrawer] = useState(null);
  const [cnrDrawer, setCnrDrawer] = useState(null);
  const [returnDrawer, setReturnDrawer] = useState(null);
  const [cancelDrawer, setCancelDrawer] = useState(null);

  // Effective location: filter dropdown overrides global currentLocation when set
  const effectiveLocation = filterLocation && filterLocation !== "ALL" ? filterLocation : currentLocation;

  const load = async () => {
    setLoading(true);
    try {
      const params = {};
      if (activeTab === "ordered") {
        params.status = "ordered,partially_received";
      } else if (activeTab === "completed") {
        params.status = "invoiced";
      } else {
        params.status = activeTab;
      }
      if (effectiveLocation && effectiveLocation !== "ALL") params.location_code = effectiveLocation;
      if (q) params.q = q;
      if (filterPriority !== "ALL") params.priority = filterPriority;
      if (filterOrderType !== "ALL") params.order_against = filterOrderType;
      if (filterModel) params.model_name = filterModel;
      if (filterFromDate) params.from_date = filterFromDate;
      if (filterToDate) params.to_date = filterToDate;
      const [list, c] = await Promise.all([
        listOrders(params),
        workbenchCounts(effectiveLocation),
      ]);
      setOrders(list);
      setCounts(c);
      setPage(1);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line
  }, [activeTab, currentLocation, filterLocation, q, filterPriority, filterOrderType, filterModel, filterFromDate, filterToDate]);

  useEffect(() => {
    setSelected({});
    setExpandedId(null);
    setOrderedChip("ALL");
  }, [activeTab]);

  // Auto-open the Cancel Order drawer when navigated with ?cancel=<orderId>
  useEffect(() => {
    const cancelId = searchParams.get("cancel");
    if (!cancelId || cancelDrawer || orders.length === 0) return;
    const target = orders.find((o) => o.id === cancelId);
    if (target) {
      setCancelDrawer(target);
      searchParams.delete("cancel");
      setSearchParams(searchParams, { replace: true });
    }
  }, [orders, searchParams, setSearchParams, cancelDrawer]);

  const selectedIds = Object.keys(selected).filter((k) => selected[k]);

  useEffect(() => {
    setPage(1);
  }, [orderedChip]);

  // Apply Ordered tab chip filter client-side, then sort
  const filteredOrders = useMemo(() => {
    if (activeTab !== "ordered") return orders;
    let filtered = orders;
    if (orderedChip !== "ALL") {
      filtered = orders.filter((o) => {
        const a = arrivalStatus(o.edd);
        switch (orderedChip) {
          case "today": return a?.kind === "today";
          case "tomorrow": return a?.kind === "tomorrow";
          case "in2": return a?.kind === "upcoming" && a.days === 2;
          case "in3": return a?.kind === "upcoming" && a.days === 3;
          case "delayed": return a?.kind === "delayed";
          case "urgent": return o.priority === "urgent";
          case "partial": return o.status === "partially_received";
          case "backordered": return (o.parts || []).some((p) => p.receive_status === "backordered");
          default: return true;
        }
      });
    }
    return sortOrderedRows(filtered);
  }, [activeTab, orderedChip, orders]);

  // Pagination removed — show every filtered order on a single page.
  const pagedOrders = filteredOrders;

  const toggleAll = (checked) => {
    const map = {};
    if (checked) pagedOrders.forEach((o) => (map[o.id] = true));
    setSelected(map);
  };
  const toggleOne = (id) => setSelected((s) => ({ ...s, [id]: !s[id] }));

  const openTemplate = async () => {
    if (selectedIds.length === 0) return toast.error("Select at least one order");
    try {
      const data = await previewTemplate(selectedIds);
      setTemplateDialog({ ...data, order_ids: selectedIds });
    } catch {
      toast.error("Failed to preview template");
    }
  };

  const downloadTemplate = async () => {
    if (!templateDialog) return;
    try {
      const r = await generateTemplate(templateDialog.order_ids);
      const url = window.URL.createObjectURL(new Blob([r.data]));
      const a = document.createElement("a");
      a.href = url;
      const cd = r.headers["content-disposition"] || "";
      const m = cd.match(/filename="?([^";]+)"?/);
      a.download = m ? m[1] : "IVA_ORDER_TEMPLATE.xlsx";
      document.body.appendChild(a);
      a.click();
      a.remove();
      toast.success("Excel downloaded");
    } catch { toast.error("Download failed"); }
  };

  const bulkMarkOrdered = async (po, edd) => {
    try {
      await markOrderedBulk({
        order_ids: templateDialog.order_ids,
        pymidol_po: po, edd,
        actor: currentUser.name, actor_role: currentUser.role,
      });
      toast.success("Selected orders marked Ordered");
      setTemplateDialog(null);
      load();
    } catch { toast.error("Failed to mark ordered"); }
  };

  const onMarkOrdered = async (id, po, edd, remarks) => {
    try {
      await markOrdered(id, { pymidol_po: po, edd, remarks, actor: currentUser.name, actor_role: currentUser.role });
      toast.success("Marked Ordered");
      setMarkOrderedDrawer(null);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  const onReceive = async (order, statuses) => {
    try {
      await receiveParts(order.id, { part_statuses: statuses, actor: currentUser.name, actor_role: currentUser.role });
      toast.success("Parts updated");
      setReceiveDrawer(null);
      load();
    } catch { toast.error("Failed to update parts"); }
  };

  const onUpdateEdd = async (id, edd, reason, notify = true) => {
    try {
      await updateEdd(id, { edd, reason, notify, actor: currentUser.name, actor_role: currentUser.role });
      toast.success("EDD updated");
      setEddDialog(null);
      load();
    } catch { toast.error("Failed"); }
  };

  const onInvoiceComplete = async (id, body) => {
    try {
      await markInvoiced(id, { ...body, actor: currentUser.name, actor_role: currentUser.role });
      toast.success("Order completed");
      setInvoiceDrawer(null);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  const onSendReminder = async (id) => {
    try {
      await sendReminder(id, { actor: currentUser.name, actor_role: currentUser.role });
      toast.success("Reminder sent");
      setReminderDrawer(null);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  const onCnrSave = async (id, body) => {
    try {
      await cnrOrder(id, { ...body, actor: currentUser.name, actor_role: currentUser.role });
      toast.success(body.mark_unclaimed ? "Marked Unclaimed" : `${body.attempt} attempt logged`);
      setCnrDrawer(null);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  const onReturnSave = async (id, body) => {
    try {
      await returnToStock(id, { ...body, actor: currentUser.name, actor_role: currentUser.role });
      toast.success("Returned to stock");
      setReturnDrawer(null);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  const onCancelSave = async (id, body) => {
    try {
      await cancelOrder(id, { ...body, actor: currentUser.name, actor_role: currentUser.role });
      toast.success(`Order cancelled · ${body.reason}`);
      setCancelDrawer(null);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed to cancel"); }
  };

  const action = async (fn, id, msg) => {
    try {
      await fn(id, { actor: currentUser.name, actor_role: currentUser.role });
      toast.success(msg);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  const onDelete = async (id) => {
    if (!window.confirm("Delete this order? This action is logged.")) return;
    try {
      await deleteOrder(id, currentUser.name, currentUser.role);
      toast.success("Order deleted");
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Cannot delete"); }
  };

  const [excelPreview, setExcelPreview] = useState(null); // {order_number, rows, order_id}

  const onGenerateExcel = async (order) => {
    try {
      const data = await previewOrderExcel(order.id);
      setExcelPreview({ ...data, order_id: order.id });
    } catch { toast.error("Preview failed"); }
  };

  const downloadSingleExcel = async () => {
    if (!excelPreview) return;
    try {
      const r = await generateOrderExcel(excelPreview.order_id);
      const url = window.URL.createObjectURL(new Blob([r.data]));
      const a = document.createElement("a");
      a.href = url;
      const cd = r.headers["content-disposition"] || "";
      const m = cd.match(/filename="?([^";]+)"?/);
      a.download = m ? m[1] : `${excelPreview.order_number}.xlsx`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      toast.success("Excel downloaded");
    } catch { toast.error("Download failed"); }
  };

  const switchTab = (key) => {
    setActiveTab(key);
    searchParams.delete("filter");
    setSearchParams(searchParams);
  };

  return (
    <div className="space-y-5" data-testid="workbench-page">
      {/* Header */}
      <div className="flex items-start justify-between">
        <div>
          <h1 className="text-[30px] font-semibold text-yamaha-800 leading-tight tracking-tight">Parts Workbench</h1>
          <p className="text-[14px] text-[#444653] mt-1">
            Main operational screen — process orders, track delivery, and manage customer pickups.
          </p>
        </div>
      </div>

      {/* Summary Cards - compact, 6 in a row */}
      <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-7 gap-3">
        {SUMMARY_CARDS.map((c) => {
          const isActive = activeTab === c.tabKey && (c.filterPriority ? filterPriority === c.filterPriority : true);
          return (
            <button
              key={c.key}
              data-testid={`summary-${c.key}`}
              onClick={() => {
                setActiveTab(c.tabKey);
                setFilterPriority(c.filterPriority || "ALL");
                searchParams.delete("filter");
                setSearchParams(searchParams);
              }}
              className={`bg-white rounded-lg px-3 py-2.5 border-l-4 ${c.accent} border-y border-r text-left transition-all hover:shadow-sm ${
                isActive ? "border-y-yamaha-700 border-r-yamaha-700 ring-1 ring-yamaha-200" : "border-y-[#e2e1eb] border-r-[#e2e1eb]"
              }`}
            >
              <div className="flex items-center gap-2">
                <c.icon className={`w-4 h-4 ${c.iconColor}`} />
                <span className="text-[26px] font-semibold leading-none text-[#1a1b22]">
                  {String(counts[c.key] ?? 0).padStart(2, "0")}
                </span>
              </div>
              <div className="text-[11px] text-[#444653] mt-1.5 truncate">{c.label}</div>
            </button>
          );
        })}
      </div>

      {/* Tabs */}
      <div className="bg-white rounded-xl border border-[#e2e1eb] overflow-hidden">
        <div className="flex border-b border-[#e2e1eb] px-2">
          {TABS.map((t) => {
            const active = activeTab === t.key;
            const cnt = counts[t.key] ?? 0;
            return (
              <button
                key={t.key}
                data-testid={`tab-${t.key}`}
                onClick={() => switchTab(t.key)}
                className={`flex items-center gap-2 px-5 py-3.5 text-[13px] font-semibold border-b-2 transition-colors ${
                  active
                    ? "border-yamaha-700 text-yamaha-700"
                    : "border-transparent text-[#444653] hover:text-yamaha-700"
                }`}
              >
                {t.label}
                {cnt > 0 && (
                  <span
                    className={`text-[10px] font-bold px-2 py-0.5 rounded-full ${
                      t.key === "delayed"
                        ? "bg-rose-600 text-white"
                        : active
                        ? "bg-yamaha-700 text-white"
                        : "bg-[#e2e1eb] text-[#444653]"
                    }`}
                  >
                    {String(cnt).padStart(2, "0")}
                  </span>
                )}
              </button>
            );
          })}
        </div>

        {/* Filter bar */}
        {activeTab === "ordered" ? (
          <div className="px-5 py-3 border-b border-[#e2e1eb] bg-[#faf8ff] space-y-2.5">
            <div className="flex flex-wrap items-center gap-3">
              <div className="flex items-center gap-1.5">
                <Filter className="w-3.5 h-3.5 text-[#747684]" />
                <span className="text-[12px] font-semibold text-[#444653]">Filters:</span>
              </div>
              <input
                data-testid="workbench-search"
                placeholder="Search customer, mobile, order #, VIN, part #..."
                value={q}
                onChange={(e) => setQ(e.target.value)}
                className="input flex-1 min-w-[220px] !py-1.5"
              />
              <input
                data-testid="filter-model"
                placeholder="Vehicle Model"
                value={filterModel}
                onChange={(e) => setFilterModel(e.target.value)}
                className="input !w-44 !py-1.5"
              />
            </div>
            <div className="flex flex-wrap items-center gap-2" data-testid="ordered-chip-bar">
              {ORDERED_CHIPS.map((c) => {
                const active = orderedChip === c.key;
                return (
                  <button
                    key={c.key}
                    data-testid={`chip-${c.key}`}
                    onClick={() => setOrderedChip(c.key)}
                    className={`px-3 py-1 rounded-full text-[11px] font-semibold border transition-colors ${
                      active
                        ? "bg-yamaha-700 text-white border-yamaha-700"
                        : "bg-white text-[#444653] border-[#e2e1eb] hover:border-yamaha-400"
                    }`}
                  >
                    {c.label}
                  </button>
                );
              })}
            </div>
          </div>
        ) : (
          <div className="px-5 py-3 border-b border-[#e2e1eb] bg-[#faf8ff] space-y-2.5">
            <div className="flex flex-wrap items-center gap-3">
              <div className="flex items-center gap-1.5">
                <Filter className="w-3.5 h-3.5 text-[#747684]" />
                <span className="text-[12px] font-semibold text-[#444653]">Filters:</span>
              </div>
              <input
                data-testid="workbench-search"
                placeholder="Search customer, mobile, order #, VIN, part #..."
                value={q}
                onChange={(e) => setQ(e.target.value)}
                className="input flex-1 min-w-[220px] !py-1.5"
              />
              <select
                data-testid="filter-location"
                value={filterLocation}
                onChange={(e) => setFilterLocation(e.target.value)}
                className="input !w-auto !py-1.5"
              >
                <option value="ALL">All Locations</option>
                {locations.map((l) => (
                  <option key={l.code} value={l.code}>{l.code} — {l.name}</option>
                ))}
              </select>
              <select
                data-testid="filter-priority"
                value={filterPriority}
                onChange={(e) => setFilterPriority(e.target.value)}
                className="input !w-auto !py-1.5"
              >
                <option value="ALL">All Priority</option>
                <option value="normal">Normal</option>
                <option value="urgent">Urgent</option>
              </select>
              <select
                data-testid="filter-order-type"
                value={filterOrderType}
                onChange={(e) => setFilterOrderType(e.target.value)}
                className="input !w-auto !py-1.5"
              >
                <option value="ALL">All Order Types</option>
                <option value="job_card">Job Card</option>
                <option value="counter_sale">Counter Sale</option>
              </select>
              <input
                data-testid="filter-from-date"
                type="date"
                value={filterFromDate}
                onChange={(e) => setFilterFromDate(e.target.value)}
                className="input !w-36 !py-1.5"
                title="From date"
              />
              <span className="text-[#747684] text-[11px]">to</span>
              <input
                data-testid="filter-to-date"
                type="date"
                value={filterToDate}
                onChange={(e) => setFilterToDate(e.target.value)}
                className="input !w-36 !py-1.5"
                title="To date"
              />
              <input
                data-testid="filter-model"
                placeholder="Vehicle Model"
                value={filterModel}
                onChange={(e) => setFilterModel(e.target.value)}
                className="input !w-36 !py-1.5"
              />
              {(q || filterPriority !== "ALL" || filterOrderType !== "ALL" || filterModel || filterLocation !== "ALL" || filterFromDate || filterToDate) && (
                <button
                  data-testid="reset-filters"
                  onClick={() => {
                    setQ("");
                    setFilterPriority("ALL");
                    setFilterOrderType("ALL");
                    setFilterModel("");
                    setFilterLocation("ALL");
                    setFilterFromDate("");
                    setFilterToDate("");
                  }}
                  className="text-[12px] text-yamaha-700 font-semibold hover:underline"
                >
                  Reset
                </button>
              )}
            </div>
          </div>
        )}

        {/* Bulk action bar */}
        {activeTab === "new_order" && selectedIds.length > 0 && (
          <div
            className="bg-yamaha-50 border-b border-yamaha-200 px-5 py-2.5 flex items-center justify-between"
            data-testid="bulk-action-bar"
          >
            <div className="text-[13px] font-medium text-yamaha-800">
              {selectedIds.length} order(s) selected
            </div>
            <div className="flex items-center gap-2">
              <button
                onClick={() => setSelected({})}
                className="text-[12px] text-yamaha-700 hover:underline"
              >
                Clear
              </button>
              <Button
                data-testid="generate-template-btn"
                size="sm"
                onClick={openTemplate}
                className="bg-yamaha-700 hover:bg-yamaha-800 text-white"
              >
                <FileSpreadsheet className="w-4 h-4 mr-2" />
                Bulk Edit
              </Button>
            </div>
          </div>
        )}

        {/* Order cards */}
        <div className="p-4 space-y-4" data-testid="orders-list">
          {filteredOrders.length === 0 ? (
            <EmptyState />
          ) : (
            <>
              {/* Select all */}
              {activeTab === "new_order" && pagedOrders.length > 0 && (
                <div className="flex items-center gap-2 px-2 pb-1">
                  <Checkbox
                    data-testid="select-all-checkbox"
                    checked={pagedOrders.every((o) => selected[o.id])}
                    onCheckedChange={toggleAll}
                  />
                  <span className="text-[11px] text-[#747684] uppercase font-semibold tracking-wider">
                    Select all
                  </span>
                </div>
              )}

              {pagedOrders.map((o) => (
                <OrderCard
                  key={o.id}
                  order={o}
                  tab={activeTab}
                  currentUser={currentUser}
                  expanded={expandedId === o.id}
                  selected={!!selected[o.id]}
                  showSelect={activeTab === "new_order"}
                  onToggleSelect={() => toggleOne(o.id)}
                  onToggleExpand={() => setExpandedId(expandedId === o.id ? null : o.id)}
                  onMarkOrdered={() => setMarkOrderedDrawer(o)}
                  onReceive={() => setReceiveDrawer(o)}
                  onUpdateEdd={() => setEddDialog(o)}
                  onInvoiced={() => setInvoiceDrawer(o)}
                  onReminder={() => setReminderDrawer(o)}
                  onCNR={() => setCnrDrawer(o)}
                  onReturn={() => setReturnDrawer(o)}
                  onView={() => setViewOrder(o)}
                  onEdit={() => navigate(`/create?edit=${o.id}`)}
                  onCancel={() => setCancelDrawer(o)}
                  onDelete={() => onDelete(o.id)}
                  onGenerateExcel={onGenerateExcel}
                  onRefresh={load}
                />
              ))}
            </>
          )}
        </div>

        {/* Total-count footer (pagination removed — all items load on one page) */}
        {filteredOrders.length > 0 && (
          <div className="px-5 py-3 border-t border-[#e2e1eb] flex items-center justify-between" data-testid="pagination">
            <div className="text-[12px] text-[#747684]">
              Showing all {filteredOrders.length} item{filteredOrders.length === 1 ? "" : "s"}
            </div>
          </div>
        )}
      </div>

      {/* Drawers / Dialogs */}
      {markOrderedDrawer && (
        <MarkOrderedDrawer order={markOrderedDrawer} onClose={() => setMarkOrderedDrawer(null)} onSubmit={onMarkOrdered} />
      )}
      {receiveDrawer && (
        <ReceivePartsDrawer order={receiveDrawer} onClose={() => setReceiveDrawer(null)} onSubmit={onReceive} />
      )}
      {eddDialog && (
        <UpdateEddDialog order={eddDialog} onClose={() => setEddDialog(null)} onSubmit={onUpdateEdd} />
      )}
      {templateDialog && (
        <TemplatePreviewDialog
          data={templateDialog}
          onClose={() => setTemplateDialog(null)}
          onDownload={downloadTemplate}
          onMarkOrdered={bulkMarkOrdered}
        />
      )}
      {viewOrder && <ViewOrderDrawer order={viewOrder} onClose={() => setViewOrder(null)} />}

      {invoiceDrawer && (
        <InvoiceCompleteDrawer
          order={invoiceDrawer}
          onClose={() => setInvoiceDrawer(null)}
          onSubmit={onInvoiceComplete}
        />
      )}
      {reminderDrawer && (
        <SendReminderDrawer
          order={reminderDrawer}
          onClose={() => setReminderDrawer(null)}
          onSubmit={onSendReminder}
        />
      )}
      {cnrDrawer && (
        <CnrDrawer
          order={cnrDrawer}
          onClose={() => setCnrDrawer(null)}
          onSubmit={onCnrSave}
        />
      )}
      {returnDrawer && (
        <ReturnToStockDrawer
          order={returnDrawer}
          onClose={() => setReturnDrawer(null)}
          onSubmit={onReturnSave}
        />
      )}
      {cancelDrawer && (
        <CancelOrderDrawer
          order={cancelDrawer}
          onClose={() => setCancelDrawer(null)}
          onSubmit={onCancelSave}
        />
      )}

      {/* Single-order Excel preview */}
      {excelPreview && (
        <Dialog open onOpenChange={() => setExcelPreview(null)}>
          <DialogContent className="max-w-xl" data-testid="single-excel-dialog">
            <DialogHeader>
              <DialogTitle>Order Excel — {excelPreview.order_number}</DialogTitle>
            </DialogHeader>
            <div className="text-[12px] text-[#747684] mb-2">
              {excelPreview.rows.length} line item(s)
            </div>
            <div className="border border-[#e2e1eb] rounded-lg max-h-72 overflow-y-auto">
              <table className="w-full text-[13px]">
                <thead>
                  <tr className="bg-[#f4f3fc]">
                    <th className="px-3 py-2 text-left">Part No</th>
                    <th className="px-3 py-2 text-left">Part Name</th>
                    <th className="px-3 py-2 text-right">Qty</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-[#e2e1eb]">
                  {excelPreview.rows.map((r, i) => (
                    <tr key={`${r.part_number || ""}-${r.part_name || ""}-${i}`}>
                      <td className="px-3 py-1.5 font-mono text-[11px]">{r.part_number || "—"}</td>
                      <td className="px-3 py-1.5">{r.part_name}</td>
                      <td className="px-3 py-1.5 text-right font-semibold">{r.qty}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setExcelPreview(null)}>Close</Button>
              <Button
                data-testid="single-excel-download"
                className="bg-yamaha-700 hover:bg-yamaha-800 text-white"
                onClick={downloadSingleExcel}
              >
                <Download className="w-4 h-4 mr-2" />
                Download Excel
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}
    </div>
  );
};

// ============== ORDER CARD ==============
const OrderCard = ({
  order, tab, currentUser, expanded, selected, showSelect, onToggleSelect, onToggleExpand,
  onMarkOrdered, onReceive, onUpdateEdd, onInvoiced, onReminder, onCNR, onReturn,
  onView, onEdit, onCancel, onDelete, onGenerateExcel, onRefresh,
}) => {
  const isAdmin = currentUser.role === "admin";
  const isExec = ["spare_executive", "parts_manager", "admin"].includes(currentUser.role);
  const arr = arrivalStatus(order.edd);
  const isDelayed = (tab === "delayed") || (arr?.kind === "delayed" && ["ordered", "partially_received"].includes(order.status));
  const isPartial = order.status === "partially_received";

  const totalParts = order.total_parts || (order.parts || []).reduce((s, p) => s + (p.qty || 0), 0);

  // ===== Dynamic primary action per state (Ordered tab) =====
  const primaryAction = (() => {
    if (!isExec) return null;
    if (tab === "new_order")
      return { fn: onMarkOrdered, label: "Mark Ordered", icon: PackageCheck, color: "bg-yamaha-700 hover:bg-yamaha-800 text-white", testid: "act-mark-ordered" };
    if (tab === "pending_pickup")
      return { fn: onInvoiced, label: "Invoice / Complete", icon: CreditCard, color: "bg-emerald-600 hover:bg-emerald-700 text-white", testid: "act-invoice-complete" };
    if (tab === "unclaimed")
      return { fn: onReturn, label: "Return To Stock", icon: RotateCcw, color: "bg-rose-600 hover:bg-rose-700 text-white", testid: "act-return-to-stock" };
    if (tab === "completed")
      return { fn: onView, label: "View Details", icon: Eye, color: "bg-white border border-[#e2e1eb] hover:bg-[#f4f3fc] text-[#444653]", testid: "act-view-details" };
    if (tab === "ordered" || tab === "delayed") {
      // Per spec:
      //   Partial          -> Receive (primary)
      //   Delayed (non-partial) -> Update EDD
      //   Upcoming ordered -> Awaiting Arrival (informational, opens Receive on click)
      if (isPartial)
        return { fn: onReceive, label: "Receive", icon: PackageCheck, color: "bg-emerald-600 hover:bg-emerald-700 text-white", testid: "act-receive" };
      if (isDelayed)
        return { fn: onUpdateEdd, label: "Update EDD", icon: Calendar, color: "bg-amber-600 hover:bg-amber-700 text-white", testid: "act-update-edd" };
      if (order.status === "ordered")
        return { fn: onReceive, label: "Awaiting Arrival", icon: Clock, color: "bg-blue-50 hover:bg-blue-100 text-blue-700 border border-blue-200", testid: "act-awaiting" };
    }
    return null;
  })();

  // ===== Status pills (max 2) =====
  // Spec: Avoid conflicting "Delayed + Fully Arrived". For Ordered tab partials show Partially Arrived + (Delayed if applicable).
  const pills = (() => {
    const out = [];
    if (tab === "ordered") {
      if (isPartial) {
        out.push({
          key: "partial",
          label: `Partially Arrived ${order.received_parts || 0}/${totalParts}`,
          cls: "bg-orange-100 text-orange-800",
          dot: "bg-orange-500",
          testid: `partially-arrived-${order.order_number}`,
        });
        if (arr?.kind === "delayed") {
          out.push({
            key: "delayed",
            label: arr.label,
            cls: arr.color,
            dot: arr.dot,
            testid: `arrival-${order.order_number}`,
          });
        }
      } else if (arr) {
        out.push({
          key: "arrival",
          label: arr.label,
          cls: arr.color,
          dot: arr.dot,
          testid: `arrival-${order.order_number}`,
        });
      }
    } else if (tab === "pending_pickup") {
      out.push({
        key: "ready",
        label: "Ready For Pickup",
        cls: "bg-emerald-100 text-emerald-800",
        dot: "bg-emerald-500",
        testid: `ready-${order.order_number}`,
      });
      const cnrLen = (order.cnr_history || []).length;
      if (cnrLen >= 3) {
        out.push({
          key: "followup-final",
          label: "Final Follow-up Done",
          cls: "bg-rose-100 text-rose-700",
          dot: "bg-rose-500",
          testid: `final-followup-${order.order_number}`,
        });
      } else if (cnrLen >= 1) {
        out.push({
          key: "followup",
          label: `Follow-up Started (${cnrLen}/3)`,
          cls: "bg-purple-100 text-purple-700",
          dot: "bg-purple-500",
          testid: `followup-${order.order_number}`,
        });
      }
    } else if (tab === "unclaimed") {
      out.push({
        key: "unclaimed",
        label: "Unclaimed",
        cls: "bg-rose-100 text-rose-700",
        dot: "bg-rose-500",
        testid: `unclaimed-${order.order_number}`,
      });
    } else if (tab === "completed") {
      out.push({
        key: "completed",
        label: "Completed",
        cls: "bg-emerald-100 text-emerald-800",
        dot: "bg-emerald-500",
        testid: `completed-${order.order_number}`,
      });
    } else {
      // new_order / delayed
      out.push({
        key: "status",
        label: STATUS_LABELS[order.status],
        cls: STATUS_COLORS[order.status],
        dot: null,
        testid: `status-${order.order_number}`,
      });
      if (tab === "delayed" && arr?.kind === "delayed") {
        // Already conveyed by red left border; skip duplicate badge in delayed tab
      }
    }
    return out.slice(0, 2);
  })();

  return (
    <div
      data-testid={`order-card-${order.order_number}`}
      className={`bg-white rounded-lg border ${
        expanded
          ? "border-yamaha-700 ring-1 ring-yamaha-200"
          : isDelayed
          ? "border-l-4 border-l-rose-500 border-[#e2e1eb]"
          : "border-[#e2e1eb]"
      } transition-shadow hover:shadow-sm`}
    >
      <div className="px-5 py-4 flex items-stretch gap-3">
        {showSelect && (
          <div className="flex items-center" onClick={(e) => e.stopPropagation()}>
            <Checkbox
              data-testid={`select-${order.order_number}`}
              checked={selected}
              onCheckedChange={onToggleSelect}
            />
          </div>
        )}

        {/* 6-column grid: 24% / 28% / 18% / 14% / 12% / 4% with 24px gap */}
        <div
          className="grid items-center flex-1 min-w-0"
          style={{ gridTemplateColumns: "24fr 28fr 18fr 14fr 12fr 4fr", columnGap: "24px" }}
        >
          {/* COL 1 — CUSTOMER (3 stacked lines, min-height for alignment) */}
          <div className="min-w-0 cursor-pointer" onClick={onToggleExpand}>
            {/* Line 1: Name | Model | Order Type */}
            <div className="flex items-center gap-1.5 min-w-0">
              <span className="text-[14px] font-semibold text-[#1a1b22] truncate">{order.customer_name}</span>
              <span className="text-[#c4c5d5] text-[11px] flex-shrink-0">|</span>
              <span className="text-[12px] text-[#444653] truncate">{order.model_name}</span>
              <span
                className={`inline-flex items-center px-1.5 py-0.5 rounded-full text-[9px] font-bold uppercase tracking-wider whitespace-nowrap flex-shrink-0 ${
                  order.order_against === "job_card"
                    ? "bg-emerald-50 text-emerald-700"
                    : "bg-yamaha-50 text-yamaha-700"
                }`}
              >
                {order.order_against === "job_card" ? "Job Card" : "Counter"}
              </span>
            </div>
            {/* Line 2: Order# | Mobile */}
            <div className="text-[11px] text-[#747684] mt-1 flex items-center gap-1.5 min-w-0">
              <span className="font-mono font-semibold text-yamaha-700 truncate">{order.order_number}</span>
              <span className="text-[#c4c5d5] flex-shrink-0">|</span>
              <span className="truncate">{order.whatsapp_number}</span>
            </div>
            {/* Line 3: Created timestamp (always the original creation time) */}
            <div
              className="text-[10.5px] text-[#747684] mt-0.5 truncate"
              data-testid={`created-at-${order.order_number}`}
              title="Original order creation timestamp"
            >
              Created: <span className="font-medium text-[#444653]">{fmtDateTime(order.created_at)}</span>
            </div>
            {/* Line 4: Urgent pill (optional) */}
            {order.priority === "urgent" && (
              <div className="mt-1.5">
                <span
                  data-testid={`urgent-pill-${order.order_number}`}
                  className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full text-[9px] font-bold uppercase tracking-wider bg-red-100 text-red-700"
                >
                  <span className="w-1 h-1 rounded-full bg-red-500" />
                  Urgent
                </span>
              </div>
            )}
          </div>

          {/* COL 2 — PARTS (single line + total badge) */}
          <div className="min-w-0 cursor-pointer overflow-hidden" onClick={onToggleExpand}>
            <div className="flex items-center gap-1.5 min-w-0 overflow-hidden">
              {(order.parts || []).slice(0, 2).map((p, i) => (
                <span key={p.id || `${p.part_number}-${i}`} className="text-[12px] text-[#1a1b22] whitespace-nowrap truncate flex-shrink min-w-0">
                  <span className="font-medium">{p.part_name}</span>
                  <span className="text-[#747684]"> x{p.qty}</span>
                  {i === 0 && (order.parts || []).length > 1 && <span className="text-[#c4c5d5] mx-1">•</span>}
                </span>
              ))}
              {(order.parts || []).length > 2 && (
                <span className="text-[11px] text-yamaha-700 font-medium whitespace-nowrap flex-shrink-0">
                  +{order.parts.length - 2} more
                </span>
              )}
            </div>
            <div
              className="mt-1.5 inline-flex items-center gap-1 px-1.5 py-0.5 bg-[#f4f3fc] rounded text-[10px] font-semibold text-[#444653]"
              title={`Total Parts: ${totalParts}`}
              data-testid={`total-parts-${order.order_number}`}
            >
              <Package className="w-3 h-3" />
              {totalParts}
              {(order.in_stock_parts > 0 || order.available_parts > 0) && (order.status === "ordered" || order.status === "partially_received") && (
                <span
                  className="ml-1.5 inline-flex items-center gap-1 px-1.5 py-0.5 rounded bg-emerald-50 text-emerald-800 border border-emerald-200"
                  title="Available for pickup = In Stock + Received"
                  data-testid={`availability-summary-${order.order_number}`}
                >
                  {order.available_parts >= totalParts
                    ? "Fully Available"
                    : `${order.available_parts || 0}/${totalParts} Available`}
                </span>
              )}
              {order.in_stock_parts > 0 && order.status === "new" && (
                <span
                  className="ml-1.5 inline-flex items-center gap-1 px-1.5 py-0.5 rounded bg-emerald-50 text-emerald-800 border border-emerald-200"
                  data-testid={`new-availability-summary-${order.order_number}`}
                >
                  {order.in_stock_parts} In Stock · {order.to_order_parts} To Order
                </span>
              )}
            </div>
          </div>

          {/* COL 3 — STATUS (max 2 badges, vertical center, equal spacing) */}
          <div className="min-w-0 flex flex-col gap-1.5 items-start">
            {pills.map((p) => (
              <span
                key={p.key}
                data-testid={p.testid}
                className={`inline-flex items-center gap-1 px-2 h-[22px] rounded-full text-[10px] font-bold uppercase whitespace-nowrap ${p.cls}`}
              >
                {p.dot && <span className={`w-1.5 h-1.5 rounded-full ${p.dot}`} />}
                {p.label}
              </span>
            ))}
          </div>

          {/* COL 4 — EDD + PO (label-value stack, left aligned) */}
          <div className="min-w-0 text-[11px] space-y-1.5">
            {tab === "pending_pickup" && order.received_at ? (
              <>
                <div>
                  <div className="text-[#747684] text-[10px] uppercase tracking-wider font-semibold">Received</div>
                  <div className="font-semibold text-[#1a1b22] truncate" data-testid={`received-at-${order.order_number}`}>
                    {fmtDateTime(order.received_at)}
                  </div>
                </div>
              </>
            ) : (
              <>
                <div>
                  <div className="text-[#747684] text-[10px] uppercase tracking-wider font-semibold">EDD</div>
                  <div className={`font-semibold truncate ${isDelayed ? "text-rose-600" : "text-[#1a1b22]"}`}>
                    {order.edd ? fmtDate(order.edd) : <span className="text-[#c4c5d5]">—</span>}
                  </div>
                </div>
                {order.pymidol_po ? (
                  <div>
                    <div className="text-[#747684] text-[10px] uppercase tracking-wider font-semibold">PO</div>
                    <div className="font-mono font-semibold text-yamaha-700 truncate">{order.pymidol_po}</div>
                  </div>
                ) : tab === "new_order" ? (
                  <div className="text-[10px] text-[#c4c5d5]">No PO yet</div>
                ) : null}
              </>
            )}
          </div>

          {/* COL 5 — ACTIONS (primary, 120x38 fixed) */}
          <div className="min-w-0 flex items-center">
            {primaryAction && (
              <button
                data-testid={`${primaryAction.testid}-${order.order_number}`}
                onClick={primaryAction.fn}
                style={{ width: "120px", height: "38px" }}
                className={`flex items-center justify-center gap-1 px-2 rounded-md text-[11px] font-semibold transition-colors ${primaryAction.color}`}
              >
                {primaryAction.label !== "Awaiting Arrival" && (
                  <primaryAction.icon className="w-3.5 h-3.5 flex-shrink-0" />
                )}
                {primaryAction.label === "Awaiting Arrival" && (
                  <Clock className="w-3 h-3 flex-shrink-0" />
                )}
                <span className="truncate">{primaryAction.label}</span>
              </button>
            )}
          </div>

          {/* COL 6 — MORE MENU (expand + dots, vertical center) */}
          <div className="flex items-center justify-end gap-0.5">
            <button
              data-testid={`act-expand-${order.order_number}`}
              onClick={onToggleExpand}
              className="p-1.5 rounded hover:bg-[#f4f3fc] text-[#444653]"
              title={expanded ? "Collapse" : "Expand"}
            >
              <ChevronDown className={`w-4 h-4 transition-transform ${expanded ? "rotate-180" : ""}`} />
            </button>
            <DropdownMenu>
              <DropdownMenuTrigger
                data-testid={`act-more-${order.order_number}`}
                className="p-1.5 rounded hover:bg-[#f4f3fc] text-[#444653]"
              >
                <MoreVertical className="w-4 h-4" />
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-48">
                <DropdownMenuItem onClick={onView} data-testid={`more-view-${order.order_number}`}>
                  <Eye className="w-3.5 h-3.5 mr-2" />View Details
                </DropdownMenuItem>
                {tab === "new_order" && isExec && (
                  <DropdownMenuItem onClick={onEdit} data-testid={`more-edit-${order.order_number}`}>
                    <Pencil className="w-3.5 h-3.5 mr-2" />Edit
                  </DropdownMenuItem>
                )}
                {(tab === "ordered" || tab === "delayed") && isExec && primaryAction?.label !== "Update EDD" && (
                  <DropdownMenuItem onClick={onUpdateEdd} data-testid={`more-edd-${order.order_number}`}>
                    <Calendar className="w-3.5 h-3.5 mr-2" />Update EDD
                  </DropdownMenuItem>
                )}
                {(tab === "ordered" || tab === "delayed") && isExec && !["Receive", "Awaiting Arrival"].includes(primaryAction?.label) && ["ordered", "partially_received"].includes(order.status) && (
                  <DropdownMenuItem onClick={onReceive} data-testid={`more-receive-${order.order_number}`}>
                    <PackageCheck className="w-3.5 h-3.5 mr-2" />Receive Parts
                  </DropdownMenuItem>
                )}
                {tab === "pending_pickup" && (
                  <>
                    <DropdownMenuItem onClick={onReminder} data-testid={`more-reminder-${order.order_number}`}>
                      <Bell className="w-3.5 h-3.5 mr-2" />Send Reminder
                    </DropdownMenuItem>
                    <DropdownMenuItem onClick={onCNR} data-testid={`more-cnr-${order.order_number}`}>
                      <PhoneOff className="w-3.5 h-3.5 mr-2" />Log Follow-up
                    </DropdownMenuItem>
                    {isExec && (
                      <DropdownMenuItem onClick={onReturn} data-testid={`more-return-${order.order_number}`}>
                        <RotateCcw className="w-3.5 h-3.5 mr-2" />Return to Stock
                      </DropdownMenuItem>
                    )}
                  </>
                )}
                {tab === "unclaimed" && isExec && (
                  <DropdownMenuItem onClick={onReturn} data-testid={`more-return-${order.order_number}`}>
                    <RotateCcw className="w-3.5 h-3.5 mr-2" />Return to Stock
                  </DropdownMenuItem>
                )}
                {isExec && ["new_order", "ordered", "delayed"].includes(tab) && ["new_order", "ordered"].includes(order.status) && (
                  <DropdownMenuItem onClick={onCancel} data-testid={`more-cancel-${order.order_number}`}>
                    <XCircle className="w-3.5 h-3.5 mr-2" />Cancel Order
                  </DropdownMenuItem>
                )}
                {isAdmin && (
                  <>
                    <DropdownMenuSeparator />
                    <DropdownMenuItem onClick={onDelete} className="text-red-600 focus:text-red-700" data-testid={`more-delete-${order.order_number}`}>
                      <Trash2 className="w-3.5 h-3.5 mr-2" />Delete (Admin)
                    </DropdownMenuItem>
                  </>
                )}
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>
      </div>

      {expanded && (
        <ExpandedSection
          order={order}
          tab={tab}
          isExec={isExec}
          currentUser={currentUser}
          onRefresh={onRefresh}
          onEdit={onEdit}
          onMarkOrdered={onMarkOrdered}
          onReceive={onReceive}
          onUpdateEdd={onUpdateEdd}
          onInvoiced={onInvoiced}
          onReminder={onReminder}
          onCNR={onCNR}
          onReturn={onReturn}
          onGenerateExcel={onGenerateExcel}
        />
      )}
    </div>
  );
};

// ============== EXPANDED SECTION (accordion) ==============
const ExpandedSection = ({
  order, tab, isExec, currentUser, onRefresh, onEdit, onMarkOrdered, onReceive, onUpdateEdd, onInvoiced, onReminder, onCNR, onReturn, onGenerateExcel,
}) => {
  const [open, setOpen] = useState({ parts: true, timeline: false, customer: false, vehicle: false, notes: false, advance: false, estimate: false });
  const [advanceDialog, setAdvanceDialog] = useState(null); // {mode: "add"|"edit", initial?}
  const toggle = (k) => setOpen((s) => ({ ...s, [k]: !s[k] }));

  // Memoise the read-only DetailGrid payloads so we don't hand down a fresh
  // array reference (and re-render every detail row) on unrelated state
  // changes like toggling an accordion.
  const customerDetails = useMemo(() => ([
    ["Customer Name", order.customer_name],
    ["Mobile Number", order.whatsapp_number],
    ["Town", order.town || "—"],
    ["Order Type", order.order_against === "job_card" ? "Job Card" : "Counter Sale"],
    ["Job Card Number", order.job_card_no || "—"],
    ["Created By", order.created_by ? `${order.created_by}${order.created_by_role ? ` (${ROLE_LABELS[order.created_by_role] || order.created_by_role})` : ""} · ${fmtDateTime(order.created_at)}` : "—"],
    ["Last Updated By", order.updated_by ? `${order.updated_by}${order.updated_by_role ? ` (${ROLE_LABELS[order.updated_by_role] || order.updated_by_role})` : ""} · ${fmtDateTime(order.updated_at)}` : fmtDateTime(order.updated_at)],
  ]), [order]);

  const vehicleDetails = useMemo(() => ([
    ["Model Name", order.model_name],
    ["VIN Number", order.vin_no || "—"],
    ["Registration Number", order.registration_no || "—"],
    ["Vehicle Color", order.color || "—"],
  ]), [order]);

  // Operational actions per tab
  const actions = (() => {
    if (tab === "new_order") {
      return [
        isExec && { label: "Mark Ordered", icon: PackageCheck, color: "bg-yamaha-700 hover:bg-yamaha-800 text-white", fn: onMarkOrdered, testid: "exp-mark-ordered" },
        { label: "Generate Order Excel", icon: FileSpreadsheet, color: "bg-white border border-[#e2e1eb] hover:bg-[#f4f3fc] text-[#1a1b22]", fn: () => onGenerateExcel(order), testid: "exp-gen-excel" },
        isExec && { label: "Edit Parts", icon: Pencil, color: "bg-white border border-[#e2e1eb] hover:bg-[#f4f3fc] text-[#1a1b22]", fn: onEdit, testid: "exp-edit-parts" },
        { label: "Activity Timeline", icon: Activity, color: "bg-white border border-[#e2e1eb] hover:bg-[#f4f3fc] text-[#1a1b22]", fn: () => toggle("timeline"), testid: "exp-timeline" },
      ].filter(Boolean);
    }
    if (tab === "ordered" || tab === "delayed") {
      return [
        isExec && ["ordered", "partially_received"].includes(order.status) && { label: "Receive Parts", icon: PackageCheck, color: "bg-emerald-600 hover:bg-emerald-700 text-white", fn: onReceive, testid: "exp-receive" },
        isExec && { label: "Update EDD", icon: Calendar, color: "bg-amber-600 hover:bg-amber-700 text-white", fn: onUpdateEdd, testid: "exp-update-edd" },
        { label: "Activity Timeline", icon: Activity, color: "bg-white border border-[#e2e1eb] hover:bg-[#f4f3fc] text-[#1a1b22]", fn: () => toggle("timeline"), testid: "exp-timeline" },
      ].filter(Boolean);
    }
    if (tab === "pending_pickup") {
      return [
        isExec && { label: "Invoice / Complete", icon: CreditCard, color: "bg-emerald-600 hover:bg-emerald-700 text-white", fn: onInvoiced, testid: "exp-invoice" },
        { label: "Send Reminder", icon: Bell, color: "bg-white border border-[#e2e1eb] hover:bg-[#f4f3fc] text-[#1a1b22]", fn: onReminder, testid: "exp-reminder" },
        { label: "Log Follow-up", icon: PhoneOff, color: "bg-white border border-[#e2e1eb] hover:bg-[#f4f3fc] text-purple-700", fn: onCNR, testid: "exp-cnr" },
        isExec && order.status === "unclaimed" && { label: "Return To Stock", icon: RotateCcw, color: "bg-white border border-rose-200 hover:bg-rose-50 text-rose-700", fn: onReturn, testid: "exp-return" },
      ].filter(Boolean);
    }
    return [];
  })();

  return (
    <div className="border-t border-[#e2e1eb] bg-[#faf8ff] p-4 grid grid-cols-12 gap-4" data-testid={`expanded-${order.order_number}`}>
      {/* Accordion left/main */}
      <div className="col-span-9 space-y-2">
        <AccordionItem
          icon={<Package className="w-3.5 h-3.5" />}
          title="Parts List"
          count={`${(order.parts || []).length} items · ${order.total_parts || (order.parts || []).reduce((s, p) => s + (p.qty || 0), 0)} units`}
          open={open.parts}
          onToggle={() => toggle("parts")}
          testid={`acc-parts-${order.order_number}`}
        >
          <div className="bg-white rounded-lg border border-[#e2e1eb] overflow-hidden">
            <table className="w-full text-[12px]">
              <thead className="bg-[#f4f3fc]">
                <tr>
                  <th className="px-3 py-2 text-left font-semibold text-[#747684] text-[10px] uppercase tracking-wider">Part Name</th>
                  <th className="px-3 py-2 text-left font-semibold text-[#747684] text-[10px] uppercase tracking-wider">Part Number</th>
                  <th className="px-3 py-2 text-right font-semibold text-[#747684] text-[10px] uppercase tracking-wider">Qty</th>
                  <th className="px-3 py-2 text-center font-semibold text-[#747684] text-[10px] uppercase tracking-wider">Availability</th>
                  <th className="px-3 py-2 text-right font-semibold text-[#747684] text-[10px] uppercase tracking-wider">Status</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-[#e2e1eb]">
                {order.parts.map((p) => {
                  const availability = p.availability || "to_order";
                  const rs = p.receive_status || "pending";
                  const statusLabel =
                    rs === "in_stock" ? "In Stock" :
                    rs === "ordered" ? "Ordered" :
                    rs === "received" ? "Received" :
                    rs === "backordered" ? "Backordered" :
                    "Pending";
                  const statusTone =
                    rs === "received" || rs === "in_stock" ? "bg-emerald-100 text-emerald-800"
                    : rs === "backordered" ? "bg-orange-100 text-orange-800"
                    : rs === "ordered" ? "bg-blue-100 text-blue-800"
                    : "bg-slate-100 text-slate-700";
                  return (
                  <tr key={p.id}>
                    <td className="px-3 py-1.5 font-medium text-[#1a1b22]">{p.part_name}</td>
                    <td className="px-3 py-1.5 font-mono text-[11px] text-[#444653]">{p.part_number || "—"}</td>
                    <td className="px-3 py-1.5 text-right font-semibold">x{p.qty}</td>
                    <td className="px-3 py-1.5 text-center">
                      <span className={`px-1.5 py-0.5 rounded text-[9.5px] font-bold uppercase tracking-wider ${
                        availability === "in_stock" ? "bg-emerald-50 text-emerald-800 border border-emerald-200"
                          : "bg-white text-[#444653] border border-[#e2e1eb]"
                      }`}>
                        {availability === "in_stock" ? "In Stock" : "To Order"}
                      </span>
                    </td>
                    <td className="px-3 py-1.5 text-right">
                      <span className={`px-1.5 py-0.5 rounded text-[9px] uppercase font-semibold ${statusTone}`}>
                        {statusLabel}
                      </span>
                    </td>
                  </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </AccordionItem>

        <AccordionItem
          icon={<Activity className="w-3.5 h-3.5" />}
          title="Activity Timeline"
          count={`${(order.timeline || []).length} events`}
          open={open.timeline}
          onToggle={() => toggle("timeline")}
          testid={`acc-timeline-${order.order_number}`}
        >
          <ol className="relative space-y-2.5 px-1">
            {(order.timeline || []).slice().reverse().map((t, idx, arr) => (
              <li key={t.id} className="pl-5 relative">
                <span className={`absolute left-0 top-1 w-2.5 h-2.5 rounded-full ${idx === 0 ? "bg-yamaha-700" : "bg-[#c4c5d5]"}`} />
                {idx < arr.length - 1 && <span className="absolute left-[5px] top-3.5 w-px h-full bg-[#e2e1eb]" />}
                <div className="text-[12px] font-semibold text-[#1a1b22]">{tlLabel(t.type)}</div>
                <div className="text-[10px] text-[#747684]">
                  {fmtDateTime(t.at)}
                  {t.actor ? (
                    <>
                      {" · by "}
                      <span className="font-medium text-[#1a1b22]">{t.actor}</span>
                      {t.actor_role ? (
                        <span className="text-[#747684]"> ({ROLE_LABELS[t.actor_role] || t.actor_role})</span>
                      ) : null}
                    </>
                  ) : null}
                </div>
                {t.message && <div className="text-[11px] text-[#444653] mt-0.5">{t.message}</div>}
              </li>
            ))}
          </ol>
        </AccordionItem>

        <AccordionItem
          icon={<User2 className="w-3.5 h-3.5" />}
          title="Customer Details"
          open={open.customer}
          onToggle={() => toggle("customer")}
          testid={`acc-customer-${order.order_number}`}
        >
          <DetailGrid items={customerDetails} />
        </AccordionItem>

        <AccordionItem
          icon={<Car className="w-3.5 h-3.5" />}
          title="Vehicle Details"
          open={open.vehicle}
          onToggle={() => toggle("vehicle")}
          testid={`acc-vehicle-${order.order_number}`}
        >
          <DetailGrid items={vehicleDetails} />
        </AccordionItem>

        {order.order_against === "job_card" && (
          <EstimateCard order={order} open={open.estimate} onToggle={() => toggle("estimate")} />
        )}

        <AdvanceCard
          order={order}
          currentUser={currentUser}
          open={open.advance}
          onToggle={() => toggle("advance")}
          onAdd={() => setAdvanceDialog({ mode: "add" })}
          onEdit={() => setAdvanceDialog({ mode: "edit", initial: order.advance })}
          onDelete={async () => {
            if (!window.confirm("Remove the advance payment from this order?")) return;
            try {
              await deleteOrderAdvance(order.id, currentUser.name, currentUser.role);
              toast.success("Advance removed");
              onRefresh && onRefresh();
            } catch (e) {
              toast.error(e?.response?.data?.detail || "Failed to remove advance");
            }
          }}
        />

        {advanceDialog && (
          <AdvanceDialog
            order={order}
            mode={advanceDialog.mode}
            initial={advanceDialog.initial}
            currentUser={currentUser}
            onClose={() => setAdvanceDialog(null)}
            onSaved={() => {
              setAdvanceDialog(null);
              onRefresh && onRefresh();
            }}
          />
        )}

        {order.internal_notes && (
          <AccordionItem
            icon={<FileText className="w-3.5 h-3.5" />}
            title="Internal Note"
            open={open.notes}
            onToggle={() => toggle("notes")}
            testid={`acc-notes-${order.order_number}`}
          >
            <div className="text-[12px] text-[#444653] italic bg-yamaha-50 border border-yamaha-100 rounded-lg p-3">
              "{order.internal_notes}"
            </div>
          </AccordionItem>
        )}
      </div>

      {/* Operational actions panel */}
      <div className="col-span-3">
        <div className="text-[10px] font-bold uppercase tracking-wider text-[#747684] mb-2 px-1">Order Actions</div>
        <div className="space-y-2">
          {actions.map((a) => (
            <button
              key={a.label}
              onClick={a.fn}
              data-testid={a.testid}
              className={`w-full ${a.color} py-2 rounded-lg flex items-center justify-center gap-2 text-[12px] font-semibold transition-colors`}
            >
              <a.icon className="w-3.5 h-3.5" />
              {a.label}
            </button>
          ))}
        </div>
      </div>
    </div>
  );
};

const AccordionItem = ({ icon, title, count, open, onToggle, children, testid }) => (
  <div className="bg-white rounded-lg border border-[#e2e1eb] overflow-hidden">
    <button
      onClick={onToggle}
      data-testid={testid}
      className="w-full px-4 py-2.5 flex items-center justify-between hover:bg-[#faf8ff]"
    >
      <div className="flex items-center gap-2">
        <div className="text-yamaha-700">{icon}</div>
        <span className="text-[12px] font-semibold text-[#1a1b22]">{title}</span>
        {count && <span className="text-[10px] text-[#747684]">· {count}</span>}
      </div>
      <ChevronDown className={`w-4 h-4 text-[#747684] transition-transform ${open ? "rotate-180" : ""}`} />
    </button>
    {open && <div className="px-4 pb-4 pt-1 animate-in fade-in slide-in-from-top-1 duration-150">{children}</div>}
  </div>
);

// ============== ESTIMATE CARD (read-only, view + download) ==============
const EstimateCard = ({ order, open, onToggle }) => {
  const estimates = (order.attachments || []).filter((a) => a.label === "estimate");
  const count = estimates.length ? `${estimates.length} file${estimates.length > 1 ? "s" : ""}` : "not uploaded";
  return (
    <AccordionItem
      icon={<Paperclip className="w-3.5 h-3.5" />}
      title="Estimate"
      count={count}
      open={open}
      onToggle={onToggle}
      testid={`acc-estimate-${order.order_number}`}
    >
      {estimates.length === 0 ? (
        <div className="text-[12px] text-[#747684] italic bg-white border border-dashed border-[#e2e1eb] rounded-lg p-3 text-center"
             data-testid={`estimate-empty-${order.order_number}`}>
          No Estimate Uploaded
        </div>
      ) : (
        <div className="space-y-2">
          {estimates.map((a) => {
            const url = downloadOrderAttachmentUrl(order.id, a.id);
            return (
              <div key={a.id}
                data-testid={`estimate-row-${a.id}`}
                className="bg-white border border-[#e2e1eb] rounded-lg p-2.5 flex items-center gap-3">
                <div className="w-8 h-8 rounded-md bg-yamaha-50 flex items-center justify-center flex-shrink-0">
                  <FileText className="w-4 h-4 text-yamaha-700" />
                </div>
                <div className="min-w-0 flex-1">
                  <div className="text-[10px] uppercase tracking-wider text-[#747684] font-semibold">
                    Estimate No
                  </div>
                  <div className="text-[12.5px] font-semibold text-[#1a1b22] truncate" title={a.filename}>
                    {a.filename || "estimate.pdf"}
                  </div>
                  <div className="text-[10.5px] text-[#747684]">
                    Uploaded {fmtDateTime(a.uploaded_at)}
                    {a.uploaded_by ? ` · by ${a.uploaded_by}` : ""}
                  </div>
                </div>
                <a
                  href={url}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid={`estimate-view-${a.id}`}
                  className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md border border-yamaha-200 bg-yamaha-50 hover:bg-yamaha-100 text-yamaha-800 text-[11.5px] font-semibold"
                >
                  <Eye className="w-3.5 h-3.5" />
                  View
                </a>
                <a
                  href={`${url}?download=1`}
                  data-testid={`estimate-download-${a.id}`}
                  className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md bg-yamaha-700 hover:bg-yamaha-800 text-white text-[11.5px] font-semibold"
                  download={a.filename || "estimate.pdf"}
                >
                  <Download className="w-3.5 h-3.5" />
                  Download
                </a>
              </div>
            );
          })}
        </div>
      )}
    </AccordionItem>
  );
};

// ============== ADVANCE CARD (inside ExpandedSection) ==============
const AdvanceCard = ({ order, currentUser, open, onToggle, onAdd, onEdit, onDelete }) => {
  const adv = order.advance || null;
  const hasAdvance = !!(adv && Number(adv.amount) > 0);
  const canManage = ["parts_manager", "admin"].includes(currentUser?.role);
  // Anyone with create/edit_order can add the FIRST advance (matches existing Create form behaviour).
  const canAdd = ["spare_executive", "parts_manager", "admin"].includes(currentUser?.role);
  const fmtAmount = (n) =>
    new Intl.NumberFormat("en-IN", { style: "currency", currency: "INR", maximumFractionDigits: 0 }).format(Number(n || 0));

  return (
    <div
      className="bg-white rounded-lg border border-[#e2e1eb] overflow-hidden"
      data-testid={`advance-card-${order.order_number}`}
    >
      <button
        onClick={onToggle}
        className="w-full px-4 py-2.5 flex items-center justify-between hover:bg-[#faf8ff]"
        data-testid={`advance-toggle-${order.order_number}`}
      >
        <div className="flex items-center gap-2 min-w-0">
          <div className="text-yamaha-700"><Wallet className="w-3.5 h-3.5" /></div>
          <span className="text-[12px] font-semibold text-[#1a1b22]">Advance Details</span>
          {hasAdvance ? (
            <span className="text-[10px] text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-full px-2 py-0.5 font-semibold">
              {fmtAmount(adv.amount)} · {String(adv.mode || "").toUpperCase()}
            </span>
          ) : (
            <span className="text-[10px] text-[#747684]">· No Advance</span>
          )}
        </div>
        <ChevronDown className={`w-4 h-4 text-[#747684] transition-transform ${open ? "rotate-180" : ""}`} />
      </button>

      {open && (
        <div className="px-4 pb-4 pt-1 animate-in fade-in slide-in-from-top-1 duration-150">
          {!hasAdvance ? (
            <div className="flex items-center justify-between gap-3">
              <span className="text-[12px] text-[#747684]">No advance has been recorded for this order.</span>
              {canAdd && (
                <button
                  onClick={onAdd}
                  data-testid={`advance-add-${order.order_number}`}
                  className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md text-[11.5px] font-semibold bg-yamaha-700 hover:bg-yamaha-800 text-white"
                >
                  <Plus className="w-3.5 h-3.5" /> Add Advance
                </button>
              )}
            </div>
          ) : (
            <div className="flex items-start justify-between gap-3">
              <div className="text-[12px] space-y-1">
                <div className="text-[15px] font-semibold text-[#1a1b22]">
                  {fmtAmount(adv.amount)}
                  <span className="text-[#747684] font-normal text-[12px] ml-2">| {String(adv.mode || "").toUpperCase()}</span>
                </div>
                <div className="text-[11.5px] text-[#747684]">
                  {adv.date ? fmtDate(adv.date) : "Date not specified"}
                </div>
                {adv.remarks && (
                  <div className="text-[11.5px] text-[#444653] italic mt-1">"{adv.remarks}"</div>
                )}
              </div>
              {canManage && (
                <div className="flex items-center gap-1.5 flex-shrink-0">
                  <button
                    onClick={onEdit}
                    data-testid={`advance-edit-${order.order_number}`}
                    className="inline-flex items-center gap-1 px-2.5 py-1.5 rounded-md text-[11px] font-semibold border border-[#e2e1eb] hover:bg-yamaha-50 text-[#1a1b22]"
                  >
                    <Pencil className="w-3 h-3" /> Edit
                  </button>
                  <button
                    onClick={onDelete}
                    data-testid={`advance-delete-${order.order_number}`}
                    className="inline-flex items-center gap-1 px-2.5 py-1.5 rounded-md text-[11px] font-semibold border border-rose-200 hover:bg-rose-50 text-rose-600"
                  >
                    <Trash2 className="w-3 h-3" /> Remove
                  </button>
                </div>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

// ============== ADVANCE DIALOG ==============
const AdvanceDialog = ({ order, mode, initial, currentUser, onClose, onSaved }) => {
  const [amount, setAmount] = useState(initial?.amount ? String(initial.amount) : "");
  const [paymentMode, setPaymentMode] = useState(initial?.mode || "cash");
  const [date, setDate] = useState(initial?.date || todayISO());
  const [remarks, setRemarks] = useState(initial?.remarks || "");
  const [saving, setSaving] = useState(false);

  const submit = async () => {
    const amt = Number(amount);
    if (!(amt > 0)) {
      toast.error("Enter an advance amount greater than 0.");
      return;
    }
    if (!paymentMode) {
      toast.error("Select a payment mode.");
      return;
    }
    setSaving(true);
    try {
      await setOrderAdvance(order.id, {
        amount: amt,
        mode: paymentMode,
        date: date || null,
        remarks: remarks || null,
        actor: currentUser.name,
        actor_role: currentUser.role,
      });
      toast.success(mode === "edit" ? "Advance updated" : "Advance added");
      onSaved && onSaved();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to save advance");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={true} onOpenChange={(v) => { if (!v && !saving) onClose(); }}>
      <DialogContent className="sm:max-w-md" data-testid="advance-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Wallet className="w-4 h-4 text-yamaha-700" />
            {mode === "edit" ? "Edit Advance" : "Add Advance"} · {order.order_number}
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-3.5 mt-1">
          <div>
            <label className="text-[11px] font-semibold uppercase tracking-wider text-[#747684] block mb-1">
              Date
            </label>
            <input
              type="date"
              data-testid="advance-date-input"
              value={date}
              onChange={(e) => setDate(e.target.value)}
              className="w-full border border-[#e2e1eb] rounded-md px-3 py-2 text-[13px]"
            />
          </div>
          <div>
            <label className="text-[11px] font-semibold uppercase tracking-wider text-[#747684] block mb-1">
              Advance Amount <span className="text-rose-600">*</span>
            </label>
            <div className="relative">
              <span className="absolute left-3 top-1/2 -translate-y-1/2 text-[13px] text-[#747684]">₹</span>
              <input
                type="number"
                data-testid="advance-amount-input"
                value={amount}
                onChange={(e) => setAmount(e.target.value)}
                placeholder="500"
                className="w-full border border-[#e2e1eb] rounded-md pl-7 pr-3 py-2 text-[13px]"
                min="1"
                step="1"
                autoFocus
              />
            </div>
          </div>
          <div>
            <label className="text-[11px] font-semibold uppercase tracking-wider text-[#747684] block mb-1">
              Payment Mode <span className="text-rose-600">*</span>
            </label>
            <div className="grid grid-cols-2 gap-2">
              {["cash", "upi"].map((m) => (
                <button
                  key={m}
                  type="button"
                  data-testid={`advance-mode-${m}`}
                  onClick={() => setPaymentMode(m)}
                  className={`px-3 py-2 rounded-md text-[12px] font-semibold border ${
                    paymentMode === m
                      ? "bg-yamaha-50 border-yamaha-400 text-yamaha-800"
                      : "border-[#e2e1eb] text-[#1a1b22] hover:bg-[#f4f3fc]"
                  }`}
                >
                  {m.toUpperCase()}
                </button>
              ))}
            </div>
          </div>
          <div>
            <label className="text-[11px] font-semibold uppercase tracking-wider text-[#747684] block mb-1">
              Remarks
            </label>
            <textarea
              data-testid="advance-remarks-input"
              value={remarks}
              onChange={(e) => setRemarks(e.target.value)}
              placeholder="e.g. First instalment, balance on pickup"
              rows={2}
              className="w-full border border-[#e2e1eb] rounded-md px-3 py-2 text-[13px] resize-none"
            />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={saving} data-testid="advance-cancel">
            Cancel
          </Button>
          <Button onClick={submit} disabled={saving} data-testid="advance-save" className="bg-yamaha-700 hover:bg-yamaha-800">
            {saving ? "Saving…" : "Save"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

const DetailGrid = ({ items }) => (
  <div className="grid grid-cols-2 gap-x-6 gap-y-2 text-[12px]">
    {items.map(([k, v]) => (
      <div key={k}>
        <div className="text-[10px] text-[#747684] uppercase tracking-wider font-semibold">{k}</div>
        <div className="text-[12px] text-[#1a1b22] mt-0.5">{v}</div>
      </div>
    ))}
  </div>
);

const tlLabel = (t) => {
  const map = {
    CREATED: "Order Created",
    UPDATED: "Order Updated",
    ORDERED: "Order Placed",
    EDD_UPDATED: "EDD Updated",
    RECEIVED: "Parts Received",
    BACKORDER: "Backorder Marked",
    WHATSAPP_SENT: "WhatsApp Sent",
    INVOICED: "Order Invoiced",
    CNR: "Log Follow-up",
    CANCELLED: "Order Cancelled",
    RETURN_TO_STOCK: "Returned to Stock",
    ADVANCE_ADDED: "Advance Added",
    ADVANCE_UPDATED: "Advance Updated",
    ADVANCE_REMOVED: "Advance Removed",
  };
  return map[t] || t;
};

const EmptyState = () => (
  <div className="py-12 text-center" data-testid="empty-state">
    <Wrench className="w-12 h-12 mx-auto mb-3 text-[#c4c5d5]" />
    <div className="text-[14px] text-[#1a1b22] font-semibold">No orders in this view yet.</div>
    <div className="text-[12px] text-[#747684] mt-1">
      Create a parts request from the sidebar to get started.
    </div>
  </div>
);

// ============== DRAWERS / DIALOGS ==============
const MarkOrderedDrawer = ({ order, onClose, onSubmit }) => {
  const [po, setPo] = useState("");
  const [edd, setEdd] = useState(todayISO());
  const [remarks, setRemarks] = useState("");
  return (
    <Sheet open onOpenChange={onClose}>
      <SheetContent className="w-[520px] sm:max-w-[520px]" data-testid="mark-ordered-drawer">
        <SheetHeader>
          <SheetTitle>Mark Ordered — {order.order_number}</SheetTitle>
          <div className="text-[12px] text-[#747684]">{order.customer_name} · {order.model_name}</div>
        </SheetHeader>
        <div className="space-y-4 py-5">
          <div>
            <label className="text-[11px] font-semibold uppercase text-[#747684]">Pymidol PO Number *</label>
            <input data-testid="po-input" value={po} onChange={(e) => setPo(e.target.value.toUpperCase())} className="input mt-1 uppercase" placeholder="PYM2345" />
          </div>
          <div>
            <label className="text-[11px] font-semibold uppercase text-[#747684]">Expected Delivery Date *</label>
            <input data-testid="edd-input" type="date" value={edd} onChange={(e) => setEdd(e.target.value)} className="input mt-1" />
          </div>
          <div>
            <label className="text-[11px] font-semibold uppercase text-[#747684]">Remarks</label>
            <textarea data-testid="remarks-input" rows="3" value={remarks} onChange={(e) => setRemarks(e.target.value)} className="input mt-1" />
          </div>
        </div>
        <SheetFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button
            data-testid="confirm-mark-ordered"
            className="bg-yamaha-700 text-white hover:bg-yamaha-800"
            onClick={() => {
              if (!po || !edd) return toast.error("PO and EDD required");
              onSubmit(order.id, po, edd, remarks);
            }}
          >
            Confirm
          </Button>
        </SheetFooter>
      </SheetContent>
    </Sheet>
  );
};

const ReceivePartsDrawer = ({ order, onClose, onSubmit }) => {
  // Only lines that were sent to Yamaha ("to_order") should appear in this drawer.
  // In-stock lines are already available for pickup and cannot be "received" again.
  const orderableParts = (order.parts || []).filter((p) => (p.availability || "to_order") !== "in_stock");
  const inStockCount = (order.parts || []).length - orderableParts.length;
  // Initialise: existing receive_status, but coerce "pending"/"ordered" -> "" so user must choose
  const [statuses, setStatuses] = useState(
    orderableParts.map((p) => ({
      id: p.id,
      receive_status: p.receive_status === "received" || p.receive_status === "backordered" ? p.receive_status : "",
    }))
  );
  const setStat = (id, val) =>
    setStatuses((s) => s.map((x) => (x.id === id ? { ...x, receive_status: val } : x)));
  const markAllArrived = () => {
    setStatuses(orderableParts.map((p) => ({ id: p.id, receive_status: "received" })));
  };
  const allArrived = statuses.length > 0 && statuses.every((s) => s.receive_status === "received");

  return (
    <Sheet open onOpenChange={onClose}>
      <SheetContent className="w-[640px] sm:max-w-[640px] flex flex-col p-0" data-testid="receive-parts-drawer">
        <div className="p-6 border-b border-[#e2e1eb]">
          <SheetHeader>
            <SheetTitle>Receive Parts — {order.order_number}</SheetTitle>
            <div className="text-[12px] text-[#747684]">{order.customer_name} · {order.model_name}</div>
          </SheetHeader>
        </div>
        <div className="px-6 py-3 border-b border-[#e2e1eb] bg-[#faf8ff] flex items-center justify-between">
          <label className="flex items-center gap-2 cursor-pointer" data-testid="mark-all-arrived">
            <Checkbox checked={allArrived} onCheckedChange={markAllArrived} />
            <span className="text-[12px] font-semibold text-[#1a1b22]">Mark All Arrived</span>
          </label>
          <span className="text-[11px] text-[#747684]">
            {statuses.filter((s) => s.receive_status === "received").length} of {statuses.length} marked received
          </span>
        </div>
        <div className="flex-1 overflow-y-auto p-6 space-y-2">
          {inStockCount > 0 && (
            <div className="mb-2 rounded-lg border border-emerald-200 bg-emerald-50 px-3 py-2 text-[11.5px] text-emerald-800">
              <b>{inStockCount}</b> line{inStockCount > 1 ? "s" : ""} already <b>In Stock</b> — not shown here. Only Yamaha-ordered lines can be marked received.
            </div>
          )}
          {orderableParts.map((p, idx) => {
            const cur = statuses.find((x) => x.id === p.id)?.receive_status || "";
            return (
              <div key={p.id} className="bg-[#f4f3fc] rounded-lg p-3" data-testid={`receive-row-${idx}`}>
                <div className="flex items-start justify-between">
                  <div className="min-w-0">
                    <div className="text-[13px] font-semibold text-[#1a1b22] truncate">{p.part_name}</div>
                    <div className="text-[11px] text-[#747684]">{p.part_number || "—"} · Qty {p.qty}</div>
                  </div>
                  <div className="flex gap-1.5 flex-shrink-0">
                    <button
                      data-testid={`receive-status-${idx}-received`}
                      onClick={() => setStat(p.id, "received")}
                      className={`px-3 py-1.5 rounded text-[11px] font-semibold uppercase border ${
                        cur === "received"
                          ? "bg-emerald-600 text-white border-emerald-600"
                          : "bg-white border-[#e2e1eb] text-[#444653] hover:border-emerald-400"
                      }`}
                    >
                      ✓ Received
                    </button>
                    <button
                      data-testid={`receive-status-${idx}-backordered`}
                      onClick={() => setStat(p.id, "backordered")}
                      className={`px-3 py-1.5 rounded text-[11px] font-semibold uppercase border ${
                        cur === "backordered"
                          ? "bg-orange-600 text-white border-orange-600"
                          : "bg-white border-[#e2e1eb] text-[#444653] hover:border-orange-400"
                      }`}
                    >
                      ⚠ Backorder
                    </button>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
        <div className="p-4 border-t border-[#e2e1eb] bg-white flex items-center justify-between gap-3">
          <div className="flex items-center gap-3 text-[11px]" data-testid="receive-footer-counts">
            <span className="inline-flex items-center gap-1.5">
              <span className="w-2 h-2 rounded-full bg-emerald-500" />
              <span className="font-semibold text-emerald-700">
                Received: {statuses.filter((s) => s.receive_status === "received").length}
              </span>
            </span>
            <span className="text-[#c4c5d5]">·</span>
            <span className="inline-flex items-center gap-1.5">
              <span className="w-2 h-2 rounded-full bg-orange-500" />
              <span className="font-semibold text-orange-700">
                Backordered: {statuses.filter((s) => s.receive_status === "backordered").length}
              </span>
            </span>
            <span className="text-[#c4c5d5]">·</span>
            <span className="text-[#747684]">of {statuses.length}</span>
          </div>
          <div className="flex items-center gap-2">
            <Button variant="outline" onClick={onClose} data-testid="receive-cancel">Cancel</Button>
            <Button
              data-testid="confirm-receive"
              className="bg-emerald-600 hover:bg-emerald-700 text-white"
              onClick={() => {
                const filtered = statuses.filter((s) => s.receive_status);
                if (filtered.length === 0) return toast.error("Mark at least one part as Received or Backordered");
                onSubmit(order, filtered);
              }}
            >
              Save
            </Button>
          </div>
        </div>
      </SheetContent>
    </Sheet>
  );
};

const UpdateEddDialog = ({ order, onClose, onSubmit }) => {
  const [edd, setEdd] = useState(order.edd || todayISO());
  const [reason, setReason] = useState("Yamaha Backorder");
  const [otherRemarks, setOtherRemarks] = useState("");
  const [notify, setNotify] = useState(true);

  const submit = () => {
    if (!edd) return toast.error("New EDD required");
    const finalReason = reason === "Other" ? (otherRemarks ? `Other: ${otherRemarks}` : "Other") : reason;
    onSubmit(order.id, edd, finalReason, notify);
  };

  // Build customer WhatsApp notification preview
  const formattedEdd = (() => {
    if (!edd) return "—";
    try {
      const [y, m, d] = edd.split("-");
      return `${d}/${m}/${y}`;
    } catch { return edd; }
  })();
  const previewMsg = `Hi ${order.customer_name}, the expected delivery for your order ${order.order_number} has been updated to ${formattedEdd}. We apologise for the delay and will keep you posted. — IVA Motors`;

  return (
    <Sheet open onOpenChange={onClose}>
      <SheetContent className="w-[520px] sm:max-w-[520px] flex flex-col p-0" data-testid="update-edd-dialog">
        <div className="px-6 py-4 border-b border-[#e2e1eb]">
          <SheetHeader>
            <SheetTitle>Update EDD — {order.order_number}</SheetTitle>
            <div className="text-[12px] text-[#747684]">
              Current EDD: {fmtDate(order.edd)} · {order.customer_name}
            </div>
          </SheetHeader>
        </div>

        <div className="flex-1 overflow-y-auto px-6 py-4 space-y-3.5">
          <div>
            <label className="text-[11px] font-semibold uppercase text-[#747684]">New EDD *</label>
            <input
              data-testid="new-edd-input"
              type="date"
              value={edd}
              onChange={(e) => setEdd(e.target.value)}
              className="input mt-1 !py-1.5 !text-[12px]"
            />
          </div>

          <div>
            <label className="text-[11px] font-semibold uppercase text-[#747684]">Reason</label>
            <select
              data-testid="edd-reason-select"
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              className="input mt-1 !py-1.5 !text-[12px] !h-8"
            >
              {EDD_REASONS.map((r) => (
                <option key={r} value={r}>{r}</option>
              ))}
            </select>
          </div>

          {reason === "Other" && (
            <div data-testid="edd-other-wrapper">
              <label className="text-[11px] font-semibold uppercase text-[#747684]">Remarks</label>
              <textarea
                data-testid="edd-other-remarks"
                rows="2"
                value={otherRemarks}
                onChange={(e) => setOtherRemarks(e.target.value)}
                className="input mt-1 !text-[12px]"
                placeholder="Specify the reason..."
              />
            </div>
          )}

          <label className="flex items-center gap-2 cursor-pointer" data-testid="notify-customer">
            <Checkbox checked={notify} onCheckedChange={setNotify} />
            <span className="text-[12px] text-[#1a1b22]">Notify customer via WhatsApp</span>
          </label>

          {notify && (
            <div
              className="bg-emerald-50/60 border border-emerald-200 rounded-lg p-3"
              data-testid="whatsapp-preview"
            >
              <div className="flex items-center gap-2 mb-1.5">
                <div className="w-5 h-5 rounded-full bg-emerald-600 flex items-center justify-center text-white">
                  <Bell className="w-3 h-3" />
                </div>
                <span className="text-[10px] font-bold uppercase tracking-wider text-emerald-700">
                  WhatsApp Preview
                </span>
              </div>
              <div className="text-[12px] text-[#1a1b22] leading-relaxed whitespace-pre-wrap">
                {previewMsg}
              </div>
              <div className="text-[10px] text-emerald-700 mt-2">
                → Sending to {order.whatsapp_number}
              </div>
            </div>
          )}
        </div>

        <div className="p-4 border-t border-[#e2e1eb] bg-white flex justify-end gap-2">
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button data-testid="confirm-edd" className="bg-yamaha-700 text-white hover:bg-yamaha-800" onClick={submit}>
            Update EDD
          </Button>
        </div>
      </SheetContent>
    </Sheet>
  );
};

const TemplatePreviewDialog = ({ data, onClose, onDownload, onMarkOrdered }) => {
  const [po, setPo] = useState("");
  const [edd, setEdd] = useState(todayISO());
  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-2xl" data-testid="template-preview-dialog">
        <DialogHeader>
          <DialogTitle>Bulk Edit · Order Preview</DialogTitle>
        </DialogHeader>
        <div className="text-[12px] text-[#747684] mb-2">
          Aggregated from {data.order_count} order(s). Duplicate part numbers are clubbed.
        </div>
        <div className="border border-[#e2e1eb] rounded-lg max-h-72 overflow-y-auto">
          <table className="w-full text-[13px]">
            <thead>
              <tr className="bg-[#f4f3fc]">
                <th className="px-3 py-2 text-left">Part No</th>
                <th className="px-3 py-2 text-left">Part Name</th>
                <th className="px-3 py-2 text-right">Order Qty</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-[#e2e1eb]">
              {data.rows.map((r, i) => (
                <tr key={`${r.part_number}-${i}`}>
                  <td className="px-3 py-1.5 font-mono">{r.part_number || "(no part #)"}</td>
                  <td className="px-3 py-1.5">{r.part_name}</td>
                  <td className="px-3 py-1.5 text-right font-semibold">{r.qty}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <div className="grid grid-cols-2 gap-3 mt-3">
          <div>
            <label className="text-[11px] font-semibold uppercase text-[#747684]">Pymidol PO Number</label>
            <input data-testid="bulk-po-input" value={po} onChange={(e) => setPo(e.target.value.toUpperCase())} className="input mt-1 uppercase" placeholder="PYM2345" />
          </div>
          <div>
            <label className="text-[11px] font-semibold uppercase text-[#747684]">EDD</label>
            <input data-testid="bulk-edd-input" type="date" value={edd} onChange={(e) => setEdd(e.target.value)} className="input mt-1" />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onDownload} data-testid="download-excel-btn">
            <FileSpreadsheet className="w-4 h-4 mr-2" />
            Download Excel
          </Button>
          <Button
            data-testid="bulk-mark-ordered-btn"
            className="bg-yamaha-700 hover:bg-yamaha-800 text-white"
            onClick={() => {
              if (!po || !edd) return toast.error("PO and EDD required");
              onMarkOrdered(po, edd);
            }}
          >
            Mark Ordered
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

const ViewOrderDrawer = ({ order, onClose }) => (
  <Sheet open onOpenChange={onClose}>
    <SheetContent className="w-[760px] sm:max-w-[760px] overflow-y-auto" data-testid="view-order-drawer">
      <SheetHeader>
        <SheetTitle>{order.order_number}</SheetTitle>
        <div className="text-[12px] text-[#747684]">{order.customer_name} · {order.model_name}</div>
      </SheetHeader>
      <div className="py-4">
        <ExpandedSection
          order={order}
          tab="view"
          isExec={false}
          onEdit={() => {}}
          onMarkOrdered={() => {}}
          onReceive={() => {}}
          onUpdateEdd={() => {}}
          onInvoiced={() => {}}
          onReminder={() => {}}
          onCNR={() => {}}
          onGenerateExcel={() => {}}
        />
      </div>
    </SheetContent>
  </Sheet>
);

// ============== INVOICE / COMPLETE DRAWER ==============
const InvoiceCompleteDrawer = ({ order, onClose, onSubmit }) => {
  const [invoiceNumber, setInvoiceNumber] = useState("");
  const [invoiceDate, setInvoiceDate] = useState(todayISO());
  const [billAmount, setBillAmount] = useState("");
  const [remarks, setRemarks] = useState("");
  const [notify, setNotify] = useState(true);

  const submit = () => {
    if (!invoiceNumber.trim()) return toast.error("Invoice Number is required");
    if (!invoiceDate) return toast.error("Invoice Date is required");
    const amt = billAmount === "" ? null : Number(billAmount);
    if (amt !== null && (isNaN(amt) || amt < 0)) return toast.error("Bill Amount must be a positive number");
    onSubmit(order.id, {
      invoice_number: invoiceNumber.trim(),
      invoice_date: invoiceDate,
      bill_amount: amt,
      remarks: remarks || undefined,
      notify,
    });
  };

  return (
    <Sheet open onOpenChange={onClose}>
      <SheetContent className="w-[520px] sm:max-w-[520px] flex flex-col p-0" data-testid="invoice-complete-drawer">
        <div className="px-6 py-4 border-b border-[#e2e1eb]">
          <SheetHeader>
            <SheetTitle>Invoice / Complete</SheetTitle>
            <div className="text-[12px] text-[#747684]">
              {order.customer_name} · {order.model_name} · {order.order_number}
            </div>
          </SheetHeader>
        </div>
        <div className="flex-1 overflow-y-auto px-6 py-4 space-y-3.5">
          <div className="bg-[#faf8ff] border border-[#e2e1eb] rounded-lg p-3">
            <div className="text-[10px] font-bold uppercase tracking-wider text-[#747684]">Customer</div>
            <div className="text-[13px] font-semibold mt-0.5">{order.customer_name}</div>
            <div className="text-[11px] text-[#444653] mt-0.5">{order.whatsapp_number}</div>
          </div>
          <div>
            <label className="text-[11px] font-semibold uppercase text-[#747684]">Invoice Number *</label>
            <input
              data-testid="invoice-number-input"
              value={invoiceNumber}
              onChange={(e) => setInvoiceNumber(e.target.value.toUpperCase())}
              placeholder="INV-2026-0001"
              className="input mt-1 !py-1.5 !text-[12px] uppercase"
            />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-[11px] font-semibold uppercase text-[#747684]">Invoice Date *</label>
              <input
                data-testid="invoice-date-input"
                type="date"
                value={invoiceDate}
                onChange={(e) => setInvoiceDate(e.target.value)}
                className="input mt-1 !py-1.5 !text-[12px]"
              />
            </div>
            <div>
              <label className="text-[11px] font-semibold uppercase text-[#747684]">Bill Amount (₹)</label>
              <div className="relative mt-1">
                <span className="absolute left-2.5 top-1/2 -translate-y-1/2 text-[12px] text-[#747684] pointer-events-none">₹</span>
                <input
                  data-testid="invoice-bill-amount-input"
                  type="number"
                  min="0"
                  step="0.01"
                  inputMode="decimal"
                  value={billAmount}
                  onChange={(e) => setBillAmount(e.target.value)}
                  placeholder="0.00"
                  className="input pl-6 !py-1.5 !text-[12px]"
                />
              </div>
            </div>
          </div>
          <div>
            <label className="text-[11px] font-semibold uppercase text-[#747684]">Completed Date / Time</label>
            <div className="text-[12px] text-[#1a1b22] mt-1 px-2 py-1.5 bg-[#f4f3fc] rounded">
              {fmtDateTime(new Date().toISOString())} IST · auto-captured on save
            </div>
          </div>
          <div>
            <label className="text-[11px] font-semibold uppercase text-[#747684]">Remarks</label>
            <textarea
              data-testid="invoice-remarks-input"
              rows="2"
              value={remarks}
              onChange={(e) => setRemarks(e.target.value)}
              className="input mt-1 !text-[12px]"
              placeholder="Optional notes for the completion record..."
            />
          </div>
          <label className="flex items-center gap-2 cursor-pointer" data-testid="invoice-notify">
            <Checkbox checked={notify} onCheckedChange={setNotify} />
            <span className="text-[12px] text-[#1a1b22]">Send WhatsApp confirmation</span>
          </label>
        </div>
        <div className="p-4 border-t border-[#e2e1eb] bg-white flex justify-end gap-2">
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button
            data-testid="confirm-complete-order"
            className="bg-emerald-600 hover:bg-emerald-700 text-white"
            onClick={submit}
          >
            Complete Order
          </Button>
        </div>
      </SheetContent>
    </Sheet>
  );
};

// ============== SEND REMINDER DRAWER ==============
const SendReminderDrawer = ({ order, onClose, onSubmit }) => {
  const previewMsg = `Dear ${order.customer_name},\n\nYour requested parts are ready for pickup at IVA Motors.\n\nOrder Number: ${order.order_number}\nVehicle: ${order.model_name}\n\nPlease visit us at your earliest convenience.\n— IVA Motors`;

  return (
    <Sheet open onOpenChange={onClose}>
      <SheetContent className="w-[520px] sm:max-w-[520px] flex flex-col p-0" data-testid="send-reminder-drawer">
        <div className="px-6 py-4 border-b border-[#e2e1eb]">
          <SheetHeader>
            <SheetTitle>Send Pickup Reminder</SheetTitle>
            <div className="text-[12px] text-[#747684]">
              {order.customer_name} · {order.whatsapp_number}
            </div>
          </SheetHeader>
        </div>
        <div className="flex-1 overflow-y-auto px-6 py-4">
          <div className="bg-emerald-50/60 border border-emerald-200 rounded-lg p-4" data-testid="reminder-preview">
            <div className="flex items-center gap-2 mb-2">
              <div className="w-5 h-5 rounded-full bg-emerald-600 flex items-center justify-center text-white">
                <Bell className="w-3 h-3" />
              </div>
              <span className="text-[10px] font-bold uppercase tracking-wider text-emerald-700">
                WhatsApp Preview
              </span>
            </div>
            <div className="text-[12px] text-[#1a1b22] leading-relaxed whitespace-pre-wrap">{previewMsg}</div>
            <div className="text-[10px] text-emerald-700 mt-3">→ Sending to {order.whatsapp_number}</div>
          </div>
          {order.last_reminder_at && (
            <div className="mt-3 text-[11px] text-[#747684]">
              Last reminder sent: <b>{fmtDateTime(order.last_reminder_at)}</b>
            </div>
          )}
        </div>
        <div className="p-4 border-t border-[#e2e1eb] bg-white flex justify-end gap-2">
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button
            data-testid="confirm-send-reminder"
            className="bg-emerald-600 hover:bg-emerald-700 text-white"
            onClick={() => onSubmit(order.id)}
          >
            Send Reminder
          </Button>
        </div>
      </SheetContent>
    </Sheet>
  );
};

// ============== CNR DRAWER ==============
const CNR_REMARKS = [
  "Switched off",
  "Asked to call later",
  "Customer travelling",
  "Vehicle sold",
  "Not interested",
  "Other",
];

const CnrDrawer = ({ order, onClose, onSubmit }) => {
  const history = order.cnr_history || [];
  const completed = history.length;
  // Compute next allowed attempt based on history
  const nextAttempt = completed === 0 ? "1st" : completed === 1 ? "2nd" : completed === 2 ? "final" : null;
  const allAttemptsDone = nextAttempt === null;
  const [remarks, setRemarks] = useState("");
  const [customRemarks, setCustomRemarks] = useState("");
  const [markUnclaimed, setMarkUnclaimed] = useState(false);

  const isOther = remarks === "Other";
  const finalRemarks = isOther ? customRemarks.trim() : remarks;

  const submit = () => {
    if (markUnclaimed) {
      // For unclaimed, remarks not strictly required but recommended.
      onSubmit(order.id, {
        attempt: nextAttempt || "final",
        remarks: finalRemarks || "Marked Unclaimed",
        mark_unclaimed: true,
      });
      return;
    }
    if (allAttemptsDone) return toast.error("All attempts exhausted. Tick 'Mark as Unclaimed' to close.");
    if (!finalRemarks) return toast.error("Call Remarks is required");
    onSubmit(order.id, {
      attempt: nextAttempt,
      remarks: finalRemarks,
      mark_unclaimed: false,
    });
  };

  const ATTEMPT_LABEL = { "1st": "1st Attempt", "2nd": "2nd Attempt", "final": "Final Attempt" };

  return (
    <Sheet open onOpenChange={onClose}>
      <SheetContent className="w-[520px] sm:max-w-[520px] flex flex-col p-0" data-testid="cnr-drawer">
        <div className="px-6 py-4 border-b border-[#e2e1eb]">
          <SheetHeader>
            <SheetTitle>Log Follow-up</SheetTitle>
            <div className="text-[12px] text-[#747684]">
              {order.customer_name} · {order.whatsapp_number}
            </div>
          </SheetHeader>
        </div>
        <div className="flex-1 overflow-y-auto px-6 py-4 space-y-3.5">
          {/* Attempt Type — auto-progressing */}
          <div>
            <label className="text-[11px] font-semibold uppercase text-[#747684]">Attempt Type *</label>
            <div
              data-testid="cnr-attempt-display"
              className={`mt-1 px-3 py-2 rounded-md border text-[12px] font-semibold flex items-center gap-2 ${
                allAttemptsDone
                  ? "bg-[#fff4f4] border-rose-200 text-rose-700"
                  : "bg-[#faf8ff] border-[#e2e1eb] text-yamaha-700"
              }`}
            >
              {allAttemptsDone ? (
                <>
                  <AlertTriangle className="w-3.5 h-3.5" />
                  Final Attempt Completed — no further attempts allowed
                </>
              ) : (
                <>
                  <span className="inline-block w-2 h-2 rounded-full bg-yamaha-700"></span>
                  {ATTEMPT_LABEL[nextAttempt]}
                  <span className="ml-auto text-[10px] font-normal text-[#747684]">
                    {completed} of 3 attempts logged
                  </span>
                </>
              )}
            </div>
          </div>

          {!allAttemptsDone && (
            <>
              <div>
                <label className="text-[11px] font-semibold uppercase text-[#747684]">Call Remarks *</label>
                <select
                  data-testid="cnr-remarks-select"
                  value={remarks}
                  onChange={(e) => setRemarks(e.target.value)}
                  className="input mt-1 !py-1.5 !text-[12px] !h-8"
                >
                  <option value="">Select reason…</option>
                  {CNR_REMARKS.map((r) => (
                    <option key={r} value={r}>{r}</option>
                  ))}
                </select>
              </div>

              {isOther && (
                <div>
                  <label className="text-[11px] font-semibold uppercase text-[#747684]">Specify Remarks *</label>
                  <textarea
                    data-testid="cnr-custom-remarks"
                    rows="2"
                    value={customRemarks}
                    onChange={(e) => setCustomRemarks(e.target.value)}
                    className="input mt-1 !text-[12px]"
                    placeholder="Specify..."
                  />
                </div>
              )}
            </>
          )}

          {/* Previous Follow-up History */}
          {history.length > 0 && (
            <div className="bg-[#faf8ff] border border-[#e2e1eb] rounded-lg p-3" data-testid="cnr-history">
              <div className="text-[10px] font-bold uppercase tracking-wider text-[#747684] mb-2">
                Previous Follow-up History
              </div>
              <div className="space-y-1.5">
                {history.map((h, i) => (
                  <div key={`${h.at || ""}-${h.attempt || ""}-${i}`} className="text-[11px] text-[#444653] flex items-start gap-2">
                    <span className="text-[#747684] text-[10px] whitespace-nowrap">{fmtDate(h.at)}</span>
                    <span className="font-semibold text-yamaha-700 uppercase whitespace-nowrap">{ATTEMPT_LABEL[h.attempt] || h.attempt}</span>
                    <span className="text-[#c4c5d5]">·</span>
                    <span className="flex-1">{h.remarks || "—"}</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          <label className="flex items-center gap-2 cursor-pointer pt-2 border-t border-[#e2e1eb]" data-testid="mark-unclaimed">
            <Checkbox checked={markUnclaimed} onCheckedChange={setMarkUnclaimed} />
            <div>
              <div className="text-[12px] font-semibold text-[#1a1b22]">Mark as Unclaimed</div>
              <div className="text-[10px] text-[#747684]">
                Closes follow-up and moves order to Unclaimed list immediately.
              </div>
            </div>
          </label>
        </div>
        <div className="p-4 border-t border-[#e2e1eb] bg-white flex justify-end gap-2">
          <Button variant="outline" onClick={onClose} data-testid="cnr-cancel">Cancel</Button>
          <Button
            data-testid="confirm-cnr"
            disabled={allAttemptsDone && !markUnclaimed}
            className="bg-purple-600 hover:bg-purple-700 text-white disabled:opacity-50"
            onClick={submit}
          >
            {markUnclaimed ? "Mark Unclaimed" : "Save Attempt"}
          </Button>
        </div>
      </SheetContent>
    </Sheet>
  );
};

// ============== RETURN TO STOCK DRAWER ==============
const RETURN_REASONS = ["Customer Cancelled", "Not Responding", "Duplicate Order", "Other"];

const ReturnToStockDrawer = ({ order, onClose, onSubmit }) => {
  const [reason, setReason] = useState("Customer Cancelled");
  const [remarks, setRemarks] = useState("");

  const submit = () => {
    if (reason === "Other" && !remarks.trim()) return toast.error("Remarks required when reason is Other");
    onSubmit(order.id, { reason, remarks: remarks || undefined });
  };

  return (
    <Sheet open onOpenChange={onClose}>
      <SheetContent className="w-[480px] sm:max-w-[480px] flex flex-col p-0" data-testid="return-to-stock-drawer">
        <div className="px-6 py-4 border-b border-[#e2e1eb]">
          <SheetHeader>
            <SheetTitle>Return To Stock</SheetTitle>
            <div className="text-[12px] text-[#747684]">
              {order.customer_name} · {order.order_number}
            </div>
          </SheetHeader>
        </div>
        <div className="flex-1 overflow-y-auto px-6 py-4 space-y-3.5">
          <div className="bg-rose-50 border border-rose-200 rounded-lg p-3 text-[11px] text-rose-700">
            This will return all parts to stock and close the order. This action cannot be undone.
          </div>
          <div>
            <label className="text-[11px] font-semibold uppercase text-[#747684]">Reason *</label>
            <select
              data-testid="return-reason-select"
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              className="input mt-1 !py-1.5 !text-[12px] !h-8"
            >
              {RETURN_REASONS.map((r) => (
                <option key={r} value={r}>{r}</option>
              ))}
            </select>
          </div>
          {reason === "Other" && (
            <div>
              <label className="text-[11px] font-semibold uppercase text-[#747684]">Remarks *</label>
              <textarea
                data-testid="return-remarks"
                rows="3"
                value={remarks}
                onChange={(e) => setRemarks(e.target.value)}
                className="input mt-1 !text-[12px]"
                placeholder="Specify the reason..."
              />
            </div>
          )}
        </div>
        <div className="p-4 border-t border-[#e2e1eb] bg-white flex justify-end gap-2">
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button
            data-testid="confirm-return"
            className="bg-rose-600 hover:bg-rose-700 text-white"
            onClick={submit}
          >
            Confirm Return
          </Button>
        </div>
      </SheetContent>
    </Sheet>
  );
};

const CANCEL_REASONS = [
  "Customer Cancelled",
  "Duplicate Request",
  "Wrong Part Ordered",
  "Wrong Order Created",
  "Stock Available Locally",
  "Order No Longer Required",
  "Other",
];

const CancelOrderDrawer = ({ order, onClose, onSubmit }) => {
  const [reason, setReason] = useState("Customer Cancelled");
  const [remarks, setRemarks] = useState("");

  const submit = () => {
    if (reason === "Other" && !remarks.trim()) {
      return toast.error("Remarks are required when reason is 'Other'");
    }
    onSubmit(order.id, { reason, remarks: remarks || undefined });
  };

  return (
    <Sheet open onOpenChange={onClose}>
      <SheetContent className="w-[480px] sm:max-w-[480px] flex flex-col p-0" data-testid="cancel-order-drawer">
        <div className="px-6 py-4 border-b border-[#e2e1eb]">
          <SheetHeader>
            <SheetTitle>Cancel Order</SheetTitle>
            <div className="text-[12px] text-[#747684]">
              {order.customer_name} · {order.order_number}
            </div>
          </SheetHeader>
        </div>
        <div className="flex-1 overflow-y-auto px-6 py-4 space-y-3.5">
          <div className="bg-rose-50 border border-rose-200 rounded-lg p-3 text-[11px] text-rose-700">
            This will mark the order as Cancelled. Status, PO number, EDD, parts list and timeline will be preserved for audit; the record is not deleted.
          </div>
          <div>
            <label className="text-[11px] font-semibold uppercase text-[#747684]">Reason *</label>
            <select
              data-testid="cancel-reason-select"
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              className="input mt-1 !py-1.5 !text-[12px] !h-8"
            >
              {CANCEL_REASONS.map((r) => (
                <option key={r} value={r}>{r}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="text-[11px] font-semibold uppercase text-[#747684]">
              Remarks {reason === "Other" ? "*" : "(optional)"}
            </label>
            <textarea
              data-testid="cancel-remarks"
              rows="3"
              value={remarks}
              onChange={(e) => setRemarks(e.target.value)}
              className="input mt-1 !text-[12px]"
              placeholder={reason === "Other" ? "Specify the reason..." : "Add additional context (optional)"}
            />
          </div>
        </div>
        <div className="p-4 border-t border-[#e2e1eb] bg-white flex justify-end gap-2">
          <Button variant="outline" data-testid="cancel-drawer-close" onClick={onClose}>Cancel</Button>
          <Button
            data-testid="confirm-cancel-order"
            className="bg-rose-600 hover:bg-rose-700 text-white"
            onClick={submit}
          >
            Confirm Cancellation
          </Button>
        </div>
      </SheetContent>
    </Sheet>
  );
};

export default Workbench;
